// Thin fetch wrapper. Picked native `fetch` over axios/ky:
// - one less dep, the API surface is small (3 endpoints in v1)
// - we get FormData/AbortSignal/streaming for free if needed later

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from './auth'

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1'

export interface TokenPair {
  access_token: string
  refresh_token: string
  token_type: string
}

// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #3
export type AvatarUrls = {
  xl: string | null
  lg: string | null
  md: string | null
  sm: string | null
}

export interface MeResponse {
  id: string
  email: string
  name: string
  surname: string | null
  role: string
  secondary_roles: string[]
  is_active: boolean
  must_change_password?: boolean
  two_fa_enabled?: boolean
  // Extended fields (populated by /me; partial on /auth/me).
  phone?: string | null
  title?: string | null
  bio?: string | null
  location?: string | null
  joined_at?: string | null
  data_nascita?: string | null
  cf?: string | null
  sesso?: 'M' | 'F' | 'A' | null
  cittadinanza?: string | null
  lingua_preferita?: 'it' | 'en'
  timezone?: string
  formato_data?: 'it' | 'en'
  valuta?: 'EUR' | 'USD'
  email_secondaria?: string | null
  whatsapp?: string | null
  residenza_via?: string | null
  residenza_cap?: string | null
  residenza_citta?: string | null
  residenza_provincia?: string | null
  emergenza_nome?: string | null
  emergenza_tel?: string | null
  professione_descrizione?: string | null
  specializzazioni?: string[]
  zone_competenza?: string[]
  tribunali_coperti?: string[]
  lingue_parlate?: string[]
  bio_md?: string | null
  notifiche_email_master?: boolean
  notifiche_email_settings?: Record<string, boolean> | null
  notifiche_desktop?: boolean
  notifiche_push?: boolean
  densita_default?: 'comoda' | 'densa'
  tema?: 'auto' | 'chiaro' | 'scuro'
  pagina_iniziale?: 'dashboard' | 'lotti' | 'clienti' | 'sopralluoghi'
  avatar?: AvatarUrls
}

export type MePatchPayload = {
  name?: string
  surname?: string | null
  phone?: string | null
  title?: string | null
  bio?: string | null
  location?: string | null
  joined_at?: string | null
  data_nascita?: string | null
  cf?: string | null
  sesso?: 'M' | 'F' | 'A' | null
  cittadinanza?: string | null
  lingua_preferita?: 'it' | 'en'
  timezone?: string
  formato_data?: 'it' | 'en'
  valuta?: 'EUR' | 'USD'
  email_secondaria?: string | null
  whatsapp?: string | null
  residenza_via?: string | null
  residenza_cap?: string | null
  residenza_citta?: string | null
  residenza_provincia?: string | null
  emergenza_nome?: string | null
  emergenza_tel?: string | null
  professione_descrizione?: string | null
  specializzazioni?: string[]
  zone_competenza?: string[]
  tribunali_coperti?: string[]
  lingue_parlate?: string[]
  bio_md?: string | null
  notifiche_email_master?: boolean
  notifiche_email_settings?: Record<string, boolean>
  notifiche_desktop?: boolean
  notifiche_push?: boolean
  densita_default?: 'comoda' | 'densa'
  tema?: 'auto' | 'chiaro' | 'scuro'
  pagina_iniziale?: 'dashboard' | 'lotti' | 'clienti' | 'sopralluoghi'
}

export type MeSession = {
  id: string
  device: string | null
  ip: string | null
  location_approx: string | null
  user_agent: string | null
  created_at: string
  last_active: string
  is_current: boolean
}

export type MeSessionListResponse = {
  items: MeSession[]
  total: number
}

export type AccessLogEntry = {
  ts: string
  ip: string | null
  ua: string | null
  esito: 'ok' | 'failed' | 'mfa_required' | string
}

export type BackupCodesResponse = {
  codes: string[]
}

export type AvatarResponse = {
  avatar: AvatarUrls
}

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

function pickErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    // FastAPI 422 returns detail as an array of validation errors
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: unknown }
      if (first && typeof first.msg === 'string') return first.msg
    }
    // Auth login: detail può essere oggetto { message, attempts_remaining }
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const msg = (detail as { message: unknown }).message
      if (typeof msg === 'string') return msg
    }
  }
  return fallback
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function rawJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as T
}

export async function login(
  email: string,
  password: string,
  rememberMe = false,
): Promise<TokenPair> {
  return rawJson<TokenPair>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, remember_me: rememberMe }),
  })
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return rawJson<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function resetPassword(
  token: string,
  new_password: string,
): Promise<{ message: string }> {
  return rawJson<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password }),
  })
}

export async function refresh(refreshToken: string): Promise<TokenPair> {
  return rawJson<TokenPair>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
}

async function fetchMeRaw(token: string): Promise<MeResponse> {
  const res = await fetch(`${BASE_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as MeResponse
}

/**
 * Fetch the current user. On 401, attempt one refresh and retry.
 * On refresh failure, clear tokens and rethrow so the caller can redirect.
 */
export async function fetchMe(): Promise<MeResponse> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }

  try {
    return await fetchMeRaw(access)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchMeRaw(tokens.access_token)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #1
export type ZoneRomaPref = { zona: string; top: boolean }
export type VicinanzaPref = { tipo: string; raggio_m: number }
export type NucleoFamiliare = {
  adulti?: number
  minori?: number
  eta_minori?: number[]
}

export type ClientRow = {
  id: string
  short_code: number
  completeness_pct?: number
  num_contratto: number | null
  data_contratto: string | null
  cognome: string
  nome: string
  email: string | null
  telefono: string | null
  residenza: string | null
  accompagnatori: string | null
  codice_fiscale: string | null
  priorita: string
  rating: string | null
  tipologia: string | null
  modalita_acquisto: string | null
  budget: number | null
  liquidita: number | null
  scopo_acquisto?: string | null
  zone_tag: string[]
  tipologie_target: string[]
  consultant_id: string | null
  consultant_name: string | null
  status: string
  client_stage: string
  note: string | null
  created_at: string
  // Spec #1 — extended fields, default-safe (null on list view).
  data_nascita?: string | null
  stato_civile?: string | null
  co_intestatario_id?: string | null
  co_intestatario_data_nascita?: string | null
  nucleo_familiare?: NucleoFamiliare | null
  tipo_abitazione?: string | null
  affitto_canone_mese?: number | null
  affitto_scadenza?: string | null
  pec?: string | null
  canale_preferito?: string | null
  whatsapp_attivo?: boolean
  privacy_marketing_ok?: boolean
  privacy_profilazione_ok?: boolean
  professione_principale?: string | null
  professione_secondaria?: string | null
  partita_iva?: string | null
  reddito_annuo?: number | null
  reddito_fonte?: string | null
  datore_lavoro?: string | null
  datore_contratto_tipo?: string | null
  datore_assunzione_data?: string | null
  isee_value?: number | null
  mutuo_pre_approvato?: boolean
  mutuo_banca?: string | null
  mutuo_importo_max?: number | null
  mutuo_tasso_taeg?: number | null
  mutuo_scadenza_validita?: string | null
  pref_comuni?: string[]
  pref_zone_roma?: ZoneRomaPref[] | null
  pref_budget_min?: number | null
  pref_budget_max?: number | null
  pref_budget_max_tetto?: number | null
  pref_superficie_min_mq?: number | null
  pref_superficie_max_mq?: number | null
  pref_vani_min?: number | null
  pref_bagni_min?: number | null
  pref_piano_terra_ok?: boolean
  pref_ascensore_obbligatorio?: boolean
  pref_ultimo_piano_ok?: boolean
  pref_piano_min?: number | null
  pref_esposizione?: string | null
  pref_tipologie?: string[]
  pref_tipologie_escluse?: string[]
  pref_libero_ok?: boolean
  pref_esecutato_ok?: boolean
  pref_locazione_residua_ok?: boolean
  pref_ristrutturazione_ok?: boolean
  pref_ristrutturazione_budget_max?: number | null
  pref_classe_energetica_min?: string | null
  pref_classe_energetica_escluse?: string[]
  pref_vicinanze?: VicinanzaPref[] | null
  pref_note_libere?: string | null
  pref_note_aggiornata_da?: string | null
  pref_note_aggiornata_at?: string | null
  note_libere_md?: string | null
  note_revisioni?: number
  note_last_edit_at?: string | null
  note_last_edit_by?: string | null
}

export type ClientListResponse = {
  items: ClientRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type ClientListParams = {
  page?: number
  page_size?: number
  q?: string
  consultant_id?: string
  priorita?: string
  rating?: string
  status?: string
  backlog_pending?: boolean
  zona_tag?: string
  sort?: string
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

async function fetchClientsRaw(
  token: string,
  params: ClientListParams,
): Promise<ClientListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    q: params.q,
    consultant_id: params.consultant_id,
    priorita: params.priorita,
    rating: params.rating,
    status: params.status,
    backlog_pending: params.backlog_pending ? 'true' : undefined,
    zona_tag: params.zona_tag,
    sort: params.sort,
  })
  const res = await fetch(`${BASE_URL}/clients${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as ClientListResponse
}

/**
 * Fetch a paginated client list. On 401, attempt one refresh and retry.
 */
export async function fetchClients(
  params: ClientListParams,
): Promise<ClientListResponse> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }

  try {
    return await fetchClientsRaw(access, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchClientsRaw(tokens.access_token, params)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

async function fetchClientRaw(token: string, id: string): Promise<ClientRow> {
  const res = await fetch(`${BASE_URL}/clients/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as ClientRow
}

/**
 * Fetch a single client by id. On 401, attempt one refresh and retry.
 */
export async function fetchClient(id: string): Promise<ClientRow> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }
  try {
    return await fetchClientRaw(access, id)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchClientRaw(tokens.access_token, id)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Clients — write (Slice 20)
// ---------------------------------------------------------------------------

export type ClientCreatePayload = {
  cognome: string
  nome: string
  email?: string | null
  telefono?: string | null
  residenza?: string | null
  accompagnatori?: string | null
  codice_fiscale?: string | null
  priorita?: 'ATTIVO' | 'MEDIO' | 'PASSIVO' | 'NI'
  rating?: 'A' | 'B' | 'C' | null
  tipologia?: 'PRIMA_CASA' | 'INVESTIMENTO' | '1CASA_INVESTIMENTO' | null
  modalita_acquisto?: 'LIQUIDITA' | 'MUTUO_LIQUIDITA' | null
  budget?: number | null
  liquidita?: number | null
  zone_tag?: string[]
  tipologie_target?: string[]
  consultant_id?: string | null
  note?: string | null
  num_contratto?: number | null
  data_contratto?: string | null
}

// Spec #1 — patchable extended fields (anagrafica, preferenze, note).
export type ClientExtendedPatch = {
  scopo_acquisto?: string | null
  data_nascita?: string | null
  stato_civile?: string | null
  co_intestatario_id?: string | null
  co_intestatario_data_nascita?: string | null
  nucleo_familiare?: NucleoFamiliare | null
  tipo_abitazione?: string | null
  affitto_canone_mese?: number | null
  affitto_scadenza?: string | null
  pec?: string | null
  canale_preferito?: string | null
  whatsapp_attivo?: boolean
  privacy_marketing_ok?: boolean
  privacy_profilazione_ok?: boolean
  professione_principale?: string | null
  professione_secondaria?: string | null
  partita_iva?: string | null
  reddito_annuo?: number | null
  reddito_fonte?: string | null
  datore_lavoro?: string | null
  datore_contratto_tipo?: string | null
  datore_assunzione_data?: string | null
  isee_value?: number | null
  mutuo_pre_approvato?: boolean
  mutuo_banca?: string | null
  mutuo_importo_max?: number | null
  mutuo_tasso_taeg?: number | null
  mutuo_scadenza_validita?: string | null
  pref_comuni?: string[]
  pref_zone_roma?: ZoneRomaPref[] | null
  pref_budget_min?: number | null
  pref_budget_max?: number | null
  pref_budget_max_tetto?: number | null
  pref_superficie_min_mq?: number | null
  pref_superficie_max_mq?: number | null
  pref_vani_min?: number | null
  pref_bagni_min?: number | null
  pref_piano_terra_ok?: boolean
  pref_ascensore_obbligatorio?: boolean
  pref_ultimo_piano_ok?: boolean
  pref_piano_min?: number | null
  pref_esposizione?: string | null
  pref_tipologie?: string[]
  pref_tipologie_escluse?: string[]
  pref_libero_ok?: boolean
  pref_esecutato_ok?: boolean
  pref_locazione_residua_ok?: boolean
  pref_ristrutturazione_ok?: boolean
  pref_ristrutturazione_budget_max?: number | null
  pref_classe_energetica_min?: string | null
  pref_classe_energetica_escluse?: string[]
  pref_vicinanze?: VicinanzaPref[] | null
  pref_note_libere?: string | null
  note_libere_md?: string | null
}

export type ClientPatchPayload = Partial<ClientCreatePayload> &
  ClientExtendedPatch & {
    status?: 'attivo' | 'sospeso' | 'archiviato'
  }

async function createClientRaw(
  token: string,
  body: ClientCreatePayload,
): Promise<ClientRow> {
  const res = await fetch(`${BASE_URL}/clients`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as ClientRow
}

export async function createClient(
  body: ClientCreatePayload,
): Promise<ClientRow> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await createClientRaw(access, body)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await createClientRaw(tokens.access_token, body)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

async function patchClientRaw(
  token: string,
  id: string,
  body: ClientPatchPayload,
): Promise<ClientRow> {
  const res = await fetch(`${BASE_URL}/clients/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as ClientRow
}

export async function patchClient(
  id: string,
  body: ClientPatchPayload,
): Promise<ClientRow> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await patchClientRaw(access, id, body)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await patchClientRaw(tokens.access_token, id, body)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

export async function patchClientStage(
  clientId: string,
  stage: string,
): Promise<ClientRow> {
  return withRefresh((token) =>
    authedFetch<ClientRow>(
      `/clients/${clientId}/stage`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage }),
      },
      token,
    ),
  )
}

export async function bulkAssignClients(
  clientIds: string[],
  consultantId: string,
): Promise<{ updated: number }> {
  return withRefresh((token) =>
    authedFetch<{ updated: number }>(
      `/clients/bulk-assign`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_ids: clientIds,
          consultant_id: consultantId,
        }),
      },
      token,
    ),
  )
}

export async function bulkSetPriorita(
  clientIds: string[],
  priorita: string,
): Promise<{ updated: number }> {
  return withRefresh((token) =>
    authedFetch<{ updated: number }>(
      `/clients/bulk-priorita`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: clientIds, priorita }),
      },
      token,
    ),
  )
}

export async function bulkArchiveClients(
  clientIds: string[],
): Promise<{ updated: number }> {
  return withRefresh((token) =>
    authedFetch<{ updated: number }>(
      `/clients/bulk-archive`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: clientIds }),
      },
      token,
    ),
  )
}

export type BulkMessagePayload = {
  client_ids: string[]
  template_code?: string
  subject?: string
  body: string
}

export type BulkMessageResult = {
  sent: number
  skipped_no_email: number
  failed: number
}

export async function bulkSendMessage(
  payload: BulkMessagePayload,
): Promise<BulkMessageResult> {
  return withRefresh((token) =>
    authedFetch<BulkMessageResult>(
      `/clients/bulk-message`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      token,
    ),
  )
}

export async function exportClientsCsv(clientIds: string[]): Promise<Blob> {
  return withRefresh(async (token) => {
    const res = await fetch(`${BASE_URL}/clients/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ client_ids: clientIds }),
    })
    if (!res.ok) {
      throw new ApiError(res.status, `Export fallito: HTTP ${res.status}`, null)
    }
    return res.blob()
  })
}

// ---------------------------------------------------------------------------
// Properties (lotti)
// ---------------------------------------------------------------------------

// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #2
export type CatastaleInfo = {
  foglio: string | null
  categoria: string | null
  particella: string | null
  classe: string | null
  subalterno: string | null
  rendita: number | null
}

export type VenditaInfo = {
  tentativo_corrente: number | null
  tentativi_totali: number | null
  ribasso_storico: Array<{ date?: string; base?: number; esito?: string }> | null
  ribasso_cumulato_pct: number | null
  valore_perizia: number | null
  valore_mercato_stima: number | null
  eur_mq: number | null
}

export type CustodeSollecito = {
  date?: string
  esito?: string
  note?: string
}

export type CustodeInfo = {
  nome: string | null
  studio: string | null
  telefono: string | null
  email: string | null
  solleciti_count: number
  ultimo_sollecito_at: string | null
  storico_solleciti: CustodeSollecito[] | null
}

export type VaniDettaglio = {
  soggiorno?: number
  camere?: number
  cucine?: number
  studio?: number
  ripostiglio?: number
}

export type PropertyRow = {
  id: string
  short_code: number
  source: string
  external_ref: string
  source_url: string | null
  source_links: { source: string; external_ref: string; url: string | null }[] | null
  rge: string | null
  lotto: string | null
  tribunale: string | null
  tipo_procedura: string | null
  giudice: string | null
  delegato: string | null
  numero_esperimento: string | null
  title: string | null
  description: string | null
  tipologia: string | null
  tipologia_target: string[]
  address: string | null
  comune: string | null
  provincia: string | null
  regione: string | null
  zona_tag: string | null
  zona_auto_status: string | null
  cap: string | null
  lat: number | null
  lng: number | null
  superficie_mq: number | null
  vani: number | null
  bagni: number | null
  piano: string | null
  classe_energetica: string | null
  prezzo_base: number | null
  offerta_minima: number | null
  rilancio_min: number | null
  cauzione: number | null
  valore_mercato: number | null
  margine_stimato: number | null
  occupazione: string | null
  problematiche: string | null
  data_asta: string | null
  termine_offerte: string | null
  modalita_vendita: string | null
  stage: string
  outcome: string | null
  consultant_id: string | null
  consultant_name: string | null
  tags: string[]
  last_synced_at: string | null
  raw_data: Record<string, unknown> | null
  created_at: string
  // Spec #2 — composition + nested catastale/vendita/custode (detail view only).
  superficie_commerciale_mq?: number | null
  superficie_calpestabile_mq?: number | null
  vani_dettaglio?: VaniDettaglio | null
  bagni_finestrati?: number | null
  cucina_tipo?: string | null
  balconi_count?: number | null
  balconi_mq?: number | null
  cantina_mq?: number | null
  cantina_piano?: string | null
  posto_auto?: string | null
  anno_costruzione?: number | null
  ascensore_presente?: boolean | null
  catastale?: CatastaleInfo | null
  vendita?: VenditaInfo | null
  custode?: CustodeInfo | null
  // Numero di Proposal draft auto_created sul lotto (badge "N clienti da
  // segnalare" su lista lotti). Default 0 — popolato solo da GET /properties.
  backlog_count?: number
  // Blocco sopralluoghi Livello Lotto (Fase 2)
  sopralluogo_block?: string | null
  // SCD Fattibilità (workflow Giulia)
  scd_status?: string | null
  scd_scadenza_redazione?: string | null
  scd_presentata_at?: string | null
  scd_consulente_sollecitato?: boolean
  scd_contattare_delegato?: boolean
  lorenzo_inviato: boolean
  lorenzo_esito: string | null
  // Tri-state: null = esito Lorenzo non ancora noto.
  mutuabile: boolean | null
  delegato_email?: string | null
  delegato_pec?: string | null
  delegato_telefono?: string | null
  delegato_indirizzo?: string | null
  scd_priority_color?: 'rosso' | 'arancione' | 'verde' | 'no_asta' | 'passata' | null
  scd_days_to_asta?: number | null
  // Residenziale vs box/posto auto/cantina/negozio/... — computed backend,
  // null = non classificabile (nessun segnale affidabile).
  is_residenziale?: boolean | null
  // Marketing (spec 2026-06-04-marketing-message)
  zona_marketing?: string | null
  descrizione_marketing?: string | null
  messaggio_marketing?: string | null
}

export type PropertyListResponse = {
  items: PropertyRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type PropertyListParams = {
  page?: number
  page_size?: number
  q?: string
  source?: string
  stage?: string
  comune?: string
  provincia?: string
  zona_tag?: string
  prezzo_min?: number
  prezzo_max?: number
  consultant_id?: string
  data_asta_from?: string
  data_asta_to?: string
  tribunale?: string
  tipologia?: string
  modalita_vendita?: string
  occupazione?: string
  residenziale?: boolean
  mutuabile?: boolean
  sort?: string
}

async function fetchPropertiesRaw(
  token: string,
  params: PropertyListParams,
): Promise<PropertyListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    q: params.q,
    source: params.source,
    stage: params.stage,
    comune: params.comune,
    provincia: params.provincia,
    zona_tag: params.zona_tag,
    prezzo_min: params.prezzo_min,
    prezzo_max: params.prezzo_max,
    consultant_id: params.consultant_id,
    data_asta_from: params.data_asta_from,
    data_asta_to: params.data_asta_to,
    tribunale: params.tribunale,
    tipologia: params.tipologia,
    modalita_vendita: params.modalita_vendita,
    occupazione: params.occupazione,
    residenziale: params.residenziale === undefined ? undefined : String(params.residenziale),
    mutuabile: params.mutuabile === undefined ? undefined : String(params.mutuabile),
    sort: params.sort,
  })
  const res = await fetch(`${BASE_URL}/properties${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as PropertyListResponse
}

/**
 * Fetch a paginated property list. On 401, attempt one refresh and retry.
 */
export async function fetchProperties(
  params: PropertyListParams,
): Promise<PropertyListResponse> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }

  try {
    return await fetchPropertiesRaw(access, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchPropertiesRaw(tokens.access_token, params)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

async function fetchPropertyRaw(token: string, id: string): Promise<PropertyRow> {
  const res = await fetch(`${BASE_URL}/properties/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as PropertyRow
}

/**
 * Fetch a single property by id. On 401, attempt one refresh and retry.
 */
export async function fetchProperty(id: string): Promise<PropertyRow> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }
  try {
    return await fetchPropertyRaw(access, id)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchPropertyRaw(tokens.access_token, id)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Properties — write (Slice 27)
// ---------------------------------------------------------------------------

// Import PVP by URL/idAnnuncio ----------------------------------------------
export type ImportPvpPayload = {
  url_or_id: string
}

export async function importPvpProperty(
  body: ImportPvpPayload,
): Promise<PropertyRow> {
  // Endpoint /import dispatcha multi-source (PVP/Astegiudiziarie/Quimmo/AN).
  // PVP scan paginated puo' durare fino a 3 min — fly proxy default
  // timeout 60s, ma fly proxy.idle_timeout su backend e' configurato piu
  // alto. Browser fetch no native timeout, ok per ora.
  return withRefresh((token) =>
    authedFetch<PropertyRow>(
      '/properties/import',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export type PropertyCreatePayload = {
  comune: string
  address: string
  tribunale: string
  rge: string
  data_asta?: string | null
  prezzo_base?: number | null
  offerta_minima?: number | null
  valore_mercato?: number | null
  superficie_mq?: number | null
  piano?: string | null
  tipologia?: string | null
  modalita_vendita?: string | null
  source_url?: string | null
  description?: string | null
  lotto?: string | null
  provincia?: string | null
  zona_tag?: string | null
}

export type PropertyDuplicateCheck = {
  duplicate: boolean
  existing_id?: string | null
  existing_short_code?: number | null
  existing_address?: string | null
  matched_on?: 'rge' | 'source_url' | null
}

export async function checkPropertyDuplicate(
  body: PropertyCreatePayload,
): Promise<PropertyDuplicateCheck> {
  return withRefresh((token) =>
    authedFetch<PropertyDuplicateCheck>(
      '/properties/check-duplicate',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function createProperty(
  body: PropertyCreatePayload,
): Promise<PropertyRow> {
  return withRefresh((token) =>
    authedFetch<PropertyRow>(
      '/properties',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}


export type PropertyPatchPayload = {
  address?: string | null
  prezzo_base?: number | null
  data_asta?: string | null
  rge?: string | null
  stage?:
    | 'bacheca'
    | 'scd_richiesta'
    | 'scd_pronta_fissare'
    | 'scd_fissate'
    | 'attesa_esito'
    | 'esito_pos_checklist'
    | 'domanda_asta'
    | 'data_vendita'
    | 'esito_neg_scd'
    | 'non_aggiudicata'
    | 'aggiudicata'
    | 'mappatura'
    | 'scartata'
  occupazione?:
    | 'libero'
    | 'in_corso_liberazione'
    | 'occupato_esecutato'
    | 'occupato_senza_titolo'
    | 'sconosciuto'
  consultant_id?: string
  analyst_id?: string
  title?: string
  description?: string
  tipologia_target?: string[]
  zona_tag?: string
  problematiche?: string | null
  sanatorie_costo?: number | null
  sanatorie_desc?: string | null
  valore_mercato?: number | null
  margine_stimato?: number | null
  has_mortgage?: boolean
  tags?: string[]
  outcome?: string | null
  final_price?: number | null
  // Spec #2 patchable extensions.
  cat_classe?: string | null
  superficie_commerciale_mq?: number | null
  superficie_calpestabile_mq?: number | null
  vani_dettaglio?: VaniDettaglio | null
  bagni_finestrati?: number | null
  cucina_tipo?: string | null
  balconi_count?: number | null
  balconi_mq?: number | null
  cantina_mq?: number | null
  cantina_piano?: string | null
  posto_auto?: string | null
  anno_costruzione?: number | null
  ascensore_presente?: boolean | null
  tentativo_corrente?: number | null
  tentativi_totali?: number | null
  ribasso_storico?: VenditaInfo['ribasso_storico']
  ribasso_cumulato_pct?: number | null
  valore_perizia?: number | null
  custode_nome?: string | null
  custode_studio?: string | null
  custode_telefono?: string | null
  custode_email?: string | null
  custode_solleciti_count?: number | null
  custode_ultimo_sollecito_at?: string | null
  custode_storico_solleciti?: CustodeSollecito[] | null
  // Marketing (spec 2026-06-04-marketing-message)
  zona_marketing?: string | null
  descrizione_marketing?: string | null
  messaggio_marketing?: string | null
  lorenzo_inviato?: boolean
  lorenzo_esito?: string | null
  mutuabile?: boolean | null
}

async function patchPropertyRaw(
  token: string,
  id: string,
  body: PropertyPatchPayload,
): Promise<PropertyRow> {
  const res = await fetch(`${BASE_URL}/properties/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as PropertyRow
}

export async function patchProperty(
  id: string,
  body: PropertyPatchPayload,
): Promise<PropertyRow> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await patchPropertyRaw(access, id, body)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await patchPropertyRaw(tokens.access_token, id, body)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Property AI Analysis

export type PropertyAnalysis = {
  id: string
  phase: string
  status: 'pending' | 'processing' | 'done' | 'failed' | 'draft' | 'validated'
  completeness: string | null
  model: string
  punteggio_rischio: number | null
  sintesi: string | null
  red_flags: string[] | null
  docs_used: { kind: string; filename: string }[] | null
  cost_usd: number | null
  error: string | null
  created_at: string
}

export async function analyzeProperty(
  propertyId: string,
  phase: 'quick' = 'quick',
  force = false,
): Promise<PropertyAnalysis> {
  return withRefresh((token) =>
    authedFetch<PropertyAnalysis>(
      `/properties/${propertyId}/analyze`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase, force }),
      },
      token,
    ),
  )
}

export async function fetchAnalyses(
  propertyId: string,
): Promise<PropertyAnalysis[]> {
  return withRefresh((token) =>
    authedFetch<PropertyAnalysis[]>(
      `/properties/${propertyId}/analyses`,
      { method: 'GET' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Matches (Slice 5)
// ---------------------------------------------------------------------------

export type ScoreBreakdown = {
  total: number
  budget_fit: number
  zone_match: number
  semantic: number
  rating_bonus: number
  rejected: string | null
}

export type MatchPropertyRow = {
  id: string
  short_code: number
  source: string
  external_ref: string
  source_url: string | null
  rge: string | null
  title: string | null
  tipologia: string | null
  address: string | null
  comune: string | null
  provincia: string | null
  zona_tag: string | null
  prezzo_base: number | null
  offerta_minima: number | null
  valore_mercato: number | null
  margine_stimato: number | null
  occupazione: string | null
  problematiche: string | null
  data_asta: string | null
  stage: string
  lorenzo_inviato: boolean
  mutuabile: boolean | null
}

export type MatchClientRowMinimal = {
  id: string
  short_code: number
  cognome: string
  nome: string
  email: string | null
  telefono: string | null
  priorita: string
  rating: string | null
  tipologia: string | null
  budget: number | null
  zone_tag: string[]
  tipologie_target: string[]
  consultant_id: string | null
  consultant_name: string | null
}

export type MatchForClientRow = {
  property: MatchPropertyRow
  score: number
  breakdown: ScoreBreakdown
}

export type MatchForPropertyRow = {
  client: MatchClientRowMinimal
  score: number
  breakdown: ScoreBreakdown
  proposal_status?: string | null
}

export type MatchListForClientResponse = {
  client_id: string
  total_candidates: number
  total_matches: number
  items: MatchForClientRow[]
}

export type MatchListForPropertyResponse = {
  property_id: string
  total_candidates: number
  total_matches: number
  items: MatchForPropertyRow[]
}

export type MatchParams = {
  limit?: number
  min_score?: number
}

async function fetchClientMatchesRaw(
  token: string,
  clientId: string,
  params: MatchParams,
): Promise<MatchListForClientResponse> {
  const qs = buildQuery({
    limit: params.limit,
    min_score: params.min_score,
  })
  const res = await fetch(`${BASE_URL}/clients/${clientId}/matches${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as MatchListForClientResponse
}

export async function fetchClientMatches(
  clientId: string,
  params: MatchParams,
): Promise<MatchListForClientResponse> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await fetchClientMatchesRaw(access, clientId, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchClientMatchesRaw(tokens.access_token, clientId, params)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

async function fetchPropertyMatchesRaw(
  token: string,
  propertyId: string,
  params: MatchParams,
): Promise<MatchListForPropertyResponse> {
  const qs = buildQuery({
    limit: params.limit,
    min_score: params.min_score,
  })
  const res = await fetch(`${BASE_URL}/properties/${propertyId}/matches${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as MatchListForPropertyResponse
}

export async function fetchPropertyMatches(
  propertyId: string,
  params: MatchParams,
): Promise<MatchListForPropertyResponse> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await fetchPropertyMatchesRaw(access, propertyId, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchPropertyMatchesRaw(
        tokens.access_token,
        propertyId,
        params,
      )
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Appointments
// ---------------------------------------------------------------------------

export type AppointmentRow = {
  id: string
  short_code: number
  kind: string
  status: string
  outcome: string | null
  property_id: string | null
  property_short_code: number | null
  property_address: string | null
  property_comune: string | null
  property_rge: string | null
  property_source_url: string | null
  client_id: string | null
  client_short_code: number | null
  client_name: string | null
  client_phone: string | null
  client_email: string | null
  property_data_asta: string | null
  owner_id: string
  owner_name: string | null
  coordinator_id: string | null
  coordinator_name: string | null
  data_richiesta: string | null
  scheduled_at: string | null
  location: string | null
  custode: string | null
  custode_telefono: string | null
  custode_email: string | null
  custode_id: string | null
  custode_ref: Professional | null
  sollecito_count: number
  notes: string | null
  comunicazioni: string | null
  feedback_post: string | null
  data_richiesta_custode: string | null
  cliente_confermato_at: string | null
  cliente_disdetto_at: string | null
  gcal_event_id: string | null
  gcal_last_synced_at: string | null
  // Workflow Fase 2
  modalita: string | null
  scadenza_conferma_cliente: string | null
  riattiva_il: string | null
  scheda_fattibilita_richiesta: boolean
  outcome_reason: string | null
  duplicated_from_id: string | null
  esito_non_registrato: boolean
  secondo_sollecito_dovuto: boolean
  primo_sollecito_cliente_at: string | null
  esito_interesse: boolean | null
  motivo_non_interesse: string | null
  created_at: string
}

export type AppointmentListResponse = {
  items: AppointmentRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type AppointmentListParams = {
  page?: number
  page_size?: number
  q?: string
  kind?: string
  status?: string
  owner_id?: string
  coordinator_id?: string
  client_id?: string
  property_id?: string
  scheduled_from?: string
  scheduled_to?: string
  richiesta_from?: string
  richiesta_to?: string
  data_asta_from?: string
  data_asta_to?: string
  created_from?: string
  created_to?: string
  include_ex_owners?: boolean
  da_gestire?: boolean
  sort?: string
}

async function fetchAppointmentsRaw(
  token: string,
  params: AppointmentListParams,
): Promise<AppointmentListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    q: params.q,
    kind: params.kind,
    status: params.status,
    owner_id: params.owner_id,
    coordinator_id: params.coordinator_id,
    client_id: params.client_id,
    property_id: params.property_id,
    scheduled_from: params.scheduled_from,
    scheduled_to: params.scheduled_to,
    richiesta_from: params.richiesta_from,
    richiesta_to: params.richiesta_to,
    data_asta_from: params.data_asta_from,
    data_asta_to: params.data_asta_to,
    created_from: params.created_from,
    created_to: params.created_to,
    include_ex_owners:
      params.include_ex_owners === true ? 'true' : undefined,
    da_gestire: params.da_gestire === true ? 'true' : undefined,
    sort: params.sort,
  })
  const res = await fetch(`${BASE_URL}/appointments${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as AppointmentListResponse
}

export async function fetchAppointments(
  params: AppointmentListParams,
): Promise<AppointmentListResponse> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }
  try {
    return await fetchAppointmentsRaw(access, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchAppointmentsRaw(tokens.access_token, params)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Appointments — write (Slice 21)
// ---------------------------------------------------------------------------

export type AppointmentKind =
  | 'sopralluogo'
  | 'call'
  | 'scheda'
  | 'asta'
  | 'app_presenza'
  | 'app_online'

export type AppointmentStatus =
  | 'richiesta_da_fare'
  | 'richiesta_fatta'
  | 'attesa_riscontro_cliente'
  | 'fissato'
  | 'non_effettuato'
  | 'effettuato'
  | 'comunicazione'
  | 'data_passata'
  | 'cancelled'
  | 'noshow'
  | 'sospeso'

export type AppointmentCreatePayload = {
  kind?: AppointmentKind
  property_id?: string
  client_id?: string
  owner_id?: string
  coordinator_id?: string
  data_richiesta?: string
  scheduled_at?: string
  duration_min?: number
  location?: string
  // null = clear field (backend accetta str | None).
  custode?: string | null
  custode_telefono?: string | null
  custode_email?: string | null
  status?: AppointmentStatus
  outcome?: string
  notes?: string
  comunicazioni?: string
  feedback_post?: string
  data_richiesta_custode?: string
  cliente_confermato_at?: string
  cliente_disdetto_at?: string
  // Workflow Fase 2 (settabili via PATCH)
  modalita?: 'fisico' | 'remoto'
  scadenza_conferma_cliente?: string
  riattiva_il?: string
  scheda_fattibilita_richiesta?: boolean
  // null = resetta flag sollecito cliente
  primo_sollecito_cliente_at?: string | null
  outcome_reason?:
    | 'disdetto_cliente'
    | 'annullato_custode'
    | 'mancata_conferma'
    | 'noshow_cliente'
    | 'annullata_consulente'
    | 'rifare'
    | null // clear (es. Riapri pratica chiusa)
  esito_interesse?: boolean | null
  motivo_non_interesse?: string | null
}

export type AppointmentPatchPayload = Partial<AppointmentCreatePayload>

export async function createAppointment(
  body: AppointmentCreatePayload,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      '/appointments',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function fetchAppointment(id: string): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(`/appointments/${id}`, {}, token),
  )
}

export async function patchAppointment(
  id: string,
  body: AppointmentPatchPayload,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      `/appointments/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function patchAppointmentCustode(
  id: string,
  custode_id: string | null,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      `/appointments/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custode_id }),
      },
      token,
    ),
  )
}

// Fase 2: richiedi SCD fattibilità sul lotto collegato (post-visita).
export async function richiediScdFattibilita(
  id: string,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      `/appointments/${id}/richiedi-scd`,
      { method: 'POST' },
      token,
    ),
  )
}

// Fase 2: nuova "Richiesta da fare" da un sopralluogo chiuso (remoto→fisico,
// fare nuova richiesta, riprogramma con snooze riattiva_il).
export async function duplicaRichiesta(
  id: string,
  riattiva_il?: string,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      `/appointments/${id}/duplica`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ riattiva_il: riattiva_il ?? null }),
      },
      token,
    ),
  )
}

export type SollecitoBody = {
  tipo: 'custode' | 'cliente'
  esito_custode?:
    | 'sopralluoghi_a_breve'
    | 'troppe_visite'
    | 'visite_terminate'
    | null
  nota?: string | null
}

// Fase 2: registra sollecito (custode/cliente) → nota auto + count.
export async function registraSollecito(
  id: string,
  body: SollecitoBody,
): Promise<AppointmentRow> {
  return withRefresh((token) =>
    authedFetch<AppointmentRow>(
      `/appointments/${id}/sollecito`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export type SopralluogoBlockReason =
  | 'non_visibile'
  | 'sospesa'
  | 'estinta'
  | 'revocata'
  | 'rinviata'
  | 'mancata_pubbl'
  | 'visite_terminate'

export const SOPRALLUOGO_BLOCK_LABEL: Record<string, string> = {
  non_visibile: 'Non si può vedere',
  sospesa: 'Asta sospesa',
  estinta: 'Asta estinta',
  revocata: 'Asta revocata',
  rinviata: 'Asta rinviata',
  mancata_pubbl: 'Mancata pubblicazione',
  visite_terminate: 'Visite terminate',
}

// Fase 2d: blocca/sblocca i sopralluoghi sul lotto (Livello Lotto).
export async function setSopralluogoBlock(
  propertyId: string,
  reason: SopralluogoBlockReason | null,
): Promise<PropertyRow> {
  return withRefresh((token) =>
    authedFetch<PropertyRow>(
      `/properties/${propertyId}/sopralluogo-block`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Pipeline (Trello kanban)
// ---------------------------------------------------------------------------

export type PipelineCard = {
  id: string
  trello_id: string
  trello_short_link: string | null
  name: string
  description: string | null
  list_name: string | null
  labels: string[]
  due: string | null
  trello_members: string[]
  checklist_total: number
  checklist_done: number
  has_attachments: boolean
  closed: boolean
}

export type PipelineStage = {
  stage: string
  label: string
  count: number
  cards: PipelineCard[]
}

export type PipelineResponse = {
  stages: PipelineStage[]
  total: number
}

export type PipelineParams = {
  q?: string
  label?: string
  member?: string
  closed?: boolean
}

// ---------------------------------------------------------------------------
// Proposals (Slice 6 — WhatsApp)
// ---------------------------------------------------------------------------

export type ProposalCreate = {
  property_id: string
  client_id: string
  channel?: 'whatsapp' | 'email' | 'telefono' | 'altro'
  template_name?: string
  note?: string
}

export type ProposalRow = {
  id: string
  short_code: number
  property_id: string
  property_short_code: number
  client_id: string
  client_short_code: number
  channel: string
  status: string
  match_score: number | null
  template_used: string | null
  message_body: string
  wa_url: string | null
  client_name: string
  client_phone: string | null
  sent_by: string
  sent_by_name: string | null
  sent_at: string | null
  auto_created: boolean
  created_at: string
  updated_at: string
  property_address?: string | null
  property_data_asta?: string | null
}

export type ProposalListResponse = {
  items: ProposalRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type ProposalListParams = {
  page?: number
  page_size?: number
  client_id?: string
  property_id?: string
  status?: string
  channel?: string
  sent_by?: string
  auto_created?: boolean
  sort?: string
}

async function authedFetch<T>(
  path: string,
  init: RequestInit,
  token: string,
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as T
}

async function withRefresh<T>(
  call: (token: string) => Promise<T>,
): Promise<T> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await call(access)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await call(tokens.access_token)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

export async function createProposal(
  body: ProposalCreate,
): Promise<ProposalRow> {
  return withRefresh((token) =>
    authedFetch<ProposalRow>(
      '/proposals',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// Bulk segnala — flag un lotto a N clienti (status='segnalato', no invio) ----
export type BulkSegnalaPayload = {
  property_id: string
  client_ids: string[]
  note?: string | null
}

export type BulkSegnalaResult = {
  created: number
  updated: number
  skipped_forbidden: number
  skipped_already_sent: number
  segnalato_total: number
}

export async function bulkSegnala(
  body: BulkSegnalaPayload,
): Promise<BulkSegnalaResult> {
  return withRefresh((token) =>
    authedFetch<BulkSegnalaResult>(
      '/proposals/bulk-segnala',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// Proposal backlog (Giulia dashboard) ---------------------------------------
export type ProposalBacklogRow = {
  consultant_id: string
  consultant_name: string
  pending: number
  overdue: number
  urgent: number
}

export type ProposalBacklogResponse = {
  rows: ProposalBacklogRow[]
  totals: { pending: number; overdue: number; urgent: number }
}

export async function fetchProposalsBacklog(): Promise<ProposalBacklogResponse> {
  return withRefresh((token) =>
    authedFetch<ProposalBacklogResponse>('/proposals/backlog', {}, token),
  )
}

// Send proposal via email (Resend) -------------------------------------------
export type SendProposalEmailPayload = {
  subject?: string
  body?: string
}

export type SendProposalEmailResponse = {
  id: string
  status: string
  sent_at: string | null
  channel: string
  email_to: string
  delivery_id: string | null
}

export async function sendProposalEmail(
  id: string,
  body: SendProposalEmailPayload = {},
): Promise<SendProposalEmailResponse> {
  return withRefresh((token) =>
    authedFetch<SendProposalEmailResponse>(
      `/proposals/${id}/send-email`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// Mark proposal sent manually (WhatsApp/altro) -------------------------------
export type MarkProposalSentPayload = {
  channel?: 'whatsapp' | 'email' | 'telefono' | 'altro'
}

export async function markProposalSent(
  id: string,
  body: MarkProposalSentPayload = {},
): Promise<unknown> {
  return withRefresh((token) =>
    authedFetch<unknown>(
      `/proposals/${id}/mark-sent`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// FSM Proposal transition -----------------------------------------------------
export type ProposalStatus =
  | 'draft'
  | 'segnalato'
  | 'sent'
  | 'read'
  | 'visit_scheduled'
  | 'visit_done'
  | 'won'
  | 'lost'
  | 'rejected'

export type TransitionProposalPayload = {
  to: Exclude<ProposalStatus, 'draft' | 'segnalato'>
  note?: string
}

export async function transitionProposal(
  id: string,
  body: TransitionProposalPayload,
): Promise<ProposalRow> {
  return withRefresh((token) =>
    authedFetch<ProposalRow>(
      `/proposals/${id}/transition`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function fetchProposals(
  params: ProposalListParams,
): Promise<ProposalListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    client_id: params.client_id,
    property_id: params.property_id,
    status: params.status,
    channel: params.channel,
    sent_by: params.sent_by,
    auto_created: params.auto_created === undefined ? undefined : String(params.auto_created),
    sort: params.sort,
  })
  return withRefresh((token) =>
    authedFetch<ProposalListResponse>(`/proposals${qs}`, {}, token),
  )
}

async function fetchPipelineRaw(
  token: string,
  params: PipelineParams,
): Promise<PipelineResponse> {
  const qs = buildQuery({
    q: params.q,
    label: params.label,
    member: params.member,
    closed: params.closed === undefined ? undefined : String(params.closed),
  })
  const res = await fetch(`${BASE_URL}/pipeline${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as PipelineResponse
}

export async function fetchPipeline(
  params: PipelineParams,
): Promise<PipelineResponse> {
  const access = getAccessToken()
  if (!access) {
    throw new ApiError(401, 'No access token')
  }
  try {
    return await fetchPipelineRaw(access, params)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await fetchPipelineRaw(tokens.access_token, params)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

// ---------------------------------------------------------------------------
// Participations + Vault (Slice 9)
// ---------------------------------------------------------------------------

export type ChecklistItem = {
  id: string
  label: string
  is_done: boolean
  done_at: string | null
  done_by: string | null
  sort_order: number
  created_at: string
  document_id: string | null
  document_filename: string | null
}

export type ParticipationRow = {
  id: string
  short_code: number
  property_id: string
  property_short_code: number
  client_id: string
  client_short_code: number
  consultant_id: string
  tipo_asta: string
  status: string
  offerta_prezzo: number | null
  rilancio_prev: number | null
  prezzo_aggiudicazione: number | null
  data_aggiudicazione: string | null
  contestazione_attiva: boolean
  contestazione_note: string | null
  avvocato_nome: string | null
  avvocato_contatto: string | null
  property_address: string | null
  property_comune: string | null
  property_data_asta: string | null
  client_name: string | null
  client_phone: string | null
  client_email: string | null
  consultant_name: string | null
  checklist_total: number
  checklist_done: number
  created_at: string
  updated_at: string
  // Post-Aggiudicazione (Giulia) — opzionali, popolati dopo aggiudicata
  post_agg_stage?: string | null
  checklist_documenti?: PostAggChecklist
  iban_saldo?: string | null
  iban_banca_emittente?: string | null
  pagamenti_log?: PagamentoLogEntry[]
  dt_deposito_at?: string | null
  liberazione_target_at?: string | null
  saldo_deadline_at?: string | null
  note_cronologia_post_agg?: string | null
  delegato_id?: string | null
  delegato_ref?: Professional | null
  notaio_id?: string | null
  notaio_ref?: Professional | null
}

export type ParticipationDetail = ParticipationRow & {
  checklist: ChecklistItem[]
  vault_count: number
}

export type ParticipationListResponse = {
  items: ParticipationRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type ParticipationListParams = {
  page?: number
  page_size?: number
  q?: string
  status?: string
  tipo_asta?: string
  consultant_id?: string
  client_id?: string
  property_id?: string
  sort?: string
}

export type ParticipationCreate = {
  property_id: string
  client_id: string
  tipo_asta?: 'telematica' | 'fisica' | 'mista' | 'sconosciuta'
  offerta_prezzo?: number
  rilancio_prev?: number
  consultant_id?: string
  // Esito iniziale (es. registrare l'aggiudicazione di un'asta passata).
  status?: string
  prezzo_aggiudicazione?: number
  data_aggiudicazione?: string
}

export type PostAggStage =
  | 'contatti_delegati'
  | 'sospesa'
  | 'richiedere_verbale'
  | 'richiesta_verbale_liberazione'
  | 'verbale_ricevuto'
  | 'conteggi_saldo'
  | 'saldo_pagato'
  | 'dt_firmato'
  | 'attesa_liberazione'
  | 'consegna'
  | 'conclusa'
  | 'non_saldate'

export type PostAggChecklistDoc = {
  required: boolean
  received: boolean
  note: string
}

export type PostAggChecklist = {
  dichiarazioni_obbligatorie?: Record<string, PostAggChecklistDoc>
  documentazione_tecnica?: Record<string, PostAggChecklistDoc>
  dati_finanziari?: {
    modalita_saldo_con_mutuo?: boolean
    beneficio_prima_casa?: boolean
    calcolo_imposte?: number | null
    oneri_cancellazione?: number | null
    compensi_delegato?: number | null
  }
  contatti?: {
    banca_nome?: string | null
    banca_funzionario_nome?: string | null
    banca_funzionario_telefono?: string | null
    banca_funzionario_email?: string | null
  }
}

export type PagamentoLogEntry = {
  data: string
  importo: number
  tipo: string
  note?: string
}

export type ParticipationPatch = Partial<{
  tipo_asta: 'telematica' | 'fisica' | 'mista' | 'sconosciuta'
  offerta_prezzo: number
  rilancio_prev: number
  status: string
  prezzo_aggiudicazione: number
  data_aggiudicazione: string
  contestazione_attiva: boolean
  contestazione_note: string
  avvocato_nome: string
  avvocato_contatto: string
  // Cambio consulente (reassign) — solo full-view
  consultant_id: string
  // Post-Aggiudicazione (Giulia)
  post_agg_stage: PostAggStage
  checklist_documenti: PostAggChecklist
  iban_saldo: string | null
  iban_banca_emittente: string | null
  pagamenti_log: PagamentoLogEntry[]
  dt_deposito_at: string | null
  liberazione_target_at: string | null
  saldo_deadline_at: string | null
  note_cronologia_post_agg: string | null
  delegato_id: string | null
  notaio_id: string | null
}>

export type VaultEntry = {
  id: string
  owner_id: string
  participation_id: string | null
  client_id: string | null
  label: string
  kind: string
  username: string | null
  note: string | null
  created_at: string
  updated_at: string
  is_unlocked: boolean
}

export type VaultCreate = {
  label: string
  kind: 'pec' | 'firma_digitale' | 'password_portale' | 'bonifico_ref' | 'altro'
  secret: string
  username?: string
  note?: string
  participation_id?: string
  client_id?: string
}

export type VaultListParams = {
  participation_id?: string
  client_id?: string
  kind?: string
}

export async function fetchParticipations(
  params: ParticipationListParams,
): Promise<ParticipationListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    q: params.q,
    status: params.status,
    tipo_asta: params.tipo_asta,
    consultant_id: params.consultant_id,
    client_id: params.client_id,
    property_id: params.property_id,
    sort: params.sort,
  })
  return withRefresh((token) =>
    authedFetch<ParticipationListResponse>(`/participations${qs}`, {}, token),
  )
}

export async function fetchParticipation(
  id: string,
): Promise<ParticipationDetail> {
  return withRefresh((token) =>
    authedFetch<ParticipationDetail>(`/participations/${id}`, {}, token),
  )
}

export async function createParticipation(
  body: ParticipationCreate,
): Promise<ParticipationDetail> {
  return withRefresh((token) =>
    authedFetch<ParticipationDetail>(
      '/participations',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function patchParticipation(
  id: string,
  body: ParticipationPatch,
): Promise<ParticipationDetail> {
  return withRefresh((token) =>
    authedFetch<ParticipationDetail>(
      `/participations/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// Post-aggiudicazione: 7-step iter Giulia.
export type PostAggStepStatus = 'pending' | 'in_corso' | 'completato'

export type PostAggStep = {
  key: string
  label: string
  status: PostAggStepStatus
  data: string | null
  responsabile_id: string | null
  note: string
}

export type PostAggResponse = {
  id: string
  participation_id: string
  steps: PostAggStep[]
  created_at: string
  updated_at: string
}

export type PostAggStepPatchPayload = {
  status?: PostAggStepStatus
  data?: string | null
  responsabile_id?: string | null
  note?: string | null
}

export async function fetchPostAggiudicazione(
  participationId: string,
): Promise<PostAggResponse> {
  return withRefresh((token) =>
    authedFetch<PostAggResponse>(
      `/participations/${participationId}/post-aggiudicazione`,
      {},
      token,
    ),
  )
}

export async function patchPostAggiudicazioneStep(
  participationId: string,
  stepKey: string,
  body: PostAggStepPatchPayload,
): Promise<PostAggResponse> {
  return withRefresh((token) =>
    authedFetch<PostAggResponse>(
      `/participations/${participationId}/post-aggiudicazione/step/${stepKey}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function addChecklistItem(
  participationId: string,
  label: string,
): Promise<ChecklistItem> {
  return withRefresh((token) =>
    authedFetch<ChecklistItem>(
      `/participations/${participationId}/checklist`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      },
      token,
    ),
  )
}

export async function toggleChecklistItem(
  participationId: string,
  itemId: string,
  isDone: boolean,
): Promise<ChecklistItem> {
  return withRefresh((token) =>
    authedFetch<ChecklistItem>(
      `/participations/${participationId}/checklist/${itemId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_done: isDone }),
      },
      token,
    ),
  )
}

export async function deleteChecklistItem(
  participationId: string,
  itemId: string,
): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(
      `/participations/${participationId}/checklist/${itemId}`,
      { method: 'DELETE' },
      token,
    ),
  )
}

export async function fetchVaultList(
  params: VaultListParams,
): Promise<VaultEntry[]> {
  const qs = buildQuery({
    participation_id: params.participation_id,
    client_id: params.client_id,
    kind: params.kind,
  })
  // Backend /vault returns paginated wrapper { items, total, page, ... }.
  // Frontend hooks/components expect raw VaultEntry[] — unwrap here.
  const response = await withRefresh((token) =>
    authedFetch<{ items: VaultEntry[]; total: number; page: number; page_size: number; pages: number }>(
      `/vault${qs}`,
      {},
      token,
    ),
  )
  return response.items
}

export async function createVaultEntry(body: VaultCreate): Promise<VaultEntry> {
  return withRefresh((token) =>
    authedFetch<VaultEntry>(
      '/vault',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function unlockVault(
  id: string,
): Promise<{ unlocked_until: string }> {
  return withRefresh((token) =>
    authedFetch<{ unlocked_until: string }>(
      `/vault/${id}/unlock`,
      { method: 'POST' },
      token,
    ),
  )
}

export async function getVaultSecret(
  id: string,
): Promise<{ secret: string; expires_at: string }> {
  return withRefresh((token) =>
    authedFetch<{ secret: string; expires_at: string }>(
      `/vault/${id}/secret`,
      {},
      token,
    ),
  )
}

export async function deleteVaultEntry(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(`/vault/${id}`, { method: 'DELETE' }, token),
  )
}

export type VaultPatchPayload = {
  label?: string
  note?: string | null
  username?: string | null
  secret?: string  // rotation: backend re-encrypts + invalida unlock state
  auto_lock_seconds?: number
}

export async function updateVaultEntry(
  id: string,
  body: VaultPatchPayload,
): Promise<VaultEntry> {
  return withRefresh((token) =>
    authedFetch<VaultEntry>(
      `/vault/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Activity log (Slice 9 audit trail)
// ---------------------------------------------------------------------------

export type ActivityEvent = {
  id: number
  user_id: string | null
  user_name: string | null
  action: string
  entity_type: string
  entity_id: string | null
  metadata: Record<string, unknown> | null
  occurred_at: string
}

export async function fetchParticipationActivity(
  id: string,
): Promise<ActivityEvent[]> {
  return withRefresh((token) =>
    authedFetch<ActivityEvent[]>(
      `/participations/${id}/activity`,
      {},
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Jobs / Scrape admin (Slice 10)
// ---------------------------------------------------------------------------

export type ScrapeSource =
  | 'astegiudiziarie'
  | 'pvp'
  | 'quimmo'
  | 'avvisinotarili'
  | 'falcoaste'
  | 'asteannunci'
  | 'ater'

// Unica fonte di verità per le etichette UI delle fonti di scraping (pagina
// admin scraping + filtro Fonte in Lotti). Record<ScrapeSource, string> =>
// aggiungere una fonte a ScrapeSource senza etichetta non compila.
export const SCRAPE_SOURCE_LABEL: Record<ScrapeSource, string> = {
  astegiudiziarie: 'Astegiudiziarie',
  pvp: 'PVP',
  quimmo: 'Quimmo',
  avvisinotarili: 'Avvisi Notarili',
  falcoaste: 'Falcoaste',
  asteannunci: 'Asteannunci',
  ater: 'ATER',
}

// properties.source ammette anche 2 valori non-scraping (CHECK constraint
// backend properties_source_check): import storico Excel e inserimento manuale.
export const PROPERTY_SOURCE_LABEL: Record<
  ScrapeSource | 'import_procedure' | 'manual',
  string
> = {
  ...SCRAPE_SOURCE_LABEL,
  import_procedure: 'Import storico',
  manual: 'Manuale',
}

export type ScrapeRun = {
  id: string
  source: string
  status: string
  started_at: string
  finished_at: string | null
  total_found: number
  new_items: number
  updated_items: number
  failed_items: number
  errors: unknown[] | null
}

export type EnqueueScrapeResponse = {
  job_id: string
  source: string
}

export async function fetchScrapeRuns(
  source?: string,
  limit: number = 20,
): Promise<ScrapeRun[]> {
  const qs = buildQuery({ source, limit })
  return withRefresh((token) =>
    authedFetch<ScrapeRun[]>(`/jobs/scrape/runs${qs}`, {}, token),
  )
}

export async function enqueueScrape(
  source: ScrapeSource,
): Promise<EnqueueScrapeResponse> {
  return withRefresh((token) =>
    authedFetch<EnqueueScrapeResponse>(
      '/jobs/scrape',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Documents (Slice 14)
// ---------------------------------------------------------------------------

export type DocumentKind =
  | 'ctu'
  | 'perizia'
  | 'planimetria'
  | 'avviso_vendita'
  | 'ordinanza'
  | 'bonifico_cauzione'
  | 'domanda_partecipazione'
  | 'documento_identita'
  | 'codice_fiscale'
  | 'procura'
  | 'dichiarazione'
  | 'firma_digitale'
  | 'altro'

export type DocumentRow = {
  id: string
  organization_id: string
  property_id: string | null
  participation_id: string | null
  client_id: string | null
  kind: string
  filename: string
  mime_type: string | null
  size_bytes: number | null
  extraction_status: string
  uploaded_at: string
  uploaded_by: string | null
  uploaded_by_name: string | null
}

export type DocumentListResponse = {
  items: DocumentRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type DocumentListParams = {
  participation_id?: string
  property_id?: string
  client_id?: string
  kind?: string
  page?: number
  page_size?: number
}

export type DocumentUploadParams = {
  file: File
  kind: DocumentKind
  participation_id?: string
  property_id?: string
  client_id?: string
  checklist_item_id?: string
}

export type DocumentUrlResponse = {
  url: string
  expires_at: string
}

async function uploadDocumentRaw(
  token: string,
  p: DocumentUploadParams,
): Promise<DocumentRow> {
  const fd = new FormData()
  fd.append('file', p.file)
  fd.append('kind', p.kind)
  if (p.participation_id) fd.append('participation_id', p.participation_id)
  if (p.property_id) fd.append('property_id', p.property_id)
  if (p.client_id) fd.append('client_id', p.client_id)
  if (p.checklist_item_id) fd.append('checklist_item_id', p.checklist_item_id)

  const res = await fetch(`${BASE_URL}/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  })
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as DocumentRow
}

export async function uploadDocument(
  p: DocumentUploadParams,
): Promise<DocumentRow> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await uploadDocumentRaw(access, p)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await uploadDocumentRaw(tokens.access_token, p)
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

export async function fetchDocuments(
  p: DocumentListParams,
): Promise<DocumentListResponse> {
  const qs = buildQuery({
    participation_id: p.participation_id,
    property_id: p.property_id,
    client_id: p.client_id,
    kind: p.kind,
    page: p.page,
    page_size: p.page_size,
  })
  return withRefresh((token) =>
    authedFetch<DocumentListResponse>(`/documents${qs}`, {}, token),
  )
}

export async function fetchDocumentUrl(
  id: string,
): Promise<DocumentUrlResponse> {
  return withRefresh((token) =>
    authedFetch<DocumentUrlResponse>(`/documents/${id}/url`, {}, token),
  )
}

export async function deleteDocument(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(`/documents/${id}`, { method: 'DELETE' }, token),
  )
}

export async function updateDocumentKind(id: string, kind: DocumentKind): Promise<DocumentRow> {
  return withRefresh((token) =>
    authedFetch<DocumentRow>(
      `/documents/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }) },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Global search (Slice 18)
// ---------------------------------------------------------------------------

export type SearchClientHit = {
  id: string
  cognome: string
  nome: string
  email: string | null
  priorita: string
  consultant_name: string | null
}

export type SearchPropertyHit = {
  id: string
  comune: string | null
  address: string | null
  prezzo_base: number | null
  stage: string
  source: string
  rge: string | null
  lotto: string | null
  piano: string | null
  superficie_mq: number | null
  tribunale: string | null
}

export type SearchParticipationHit = {
  id: string
  status: string
  client_name: string | null
  property_address: string | null
  property_comune: string | null
}

export type SearchResponse = {
  clients: SearchClientHit[]
  properties: SearchPropertyHit[]
  participations: SearchParticipationHit[]
  total: number
}

export async function fetchSearch(
  q: string,
  limit: number = 5,
): Promise<SearchResponse> {
  const qs = buildQuery({ q, limit })
  return withRefresh((token) =>
    authedFetch<SearchResponse>(`/search${qs}`, {}, token),
  )
}

// ---------------------------------------------------------------------------
// Google Calendar (Slice 23)
// ---------------------------------------------------------------------------

export type CalendarStatusResponse = {
  connected: boolean
  scope: string | null
  expires_at: string | null
}

export type CalendarOAuthConnectResponse = {
  url: string
}

export type CalendarSyncResponse = {
  id: string
  gcal_event_id: string | null
  gcal_last_synced_at: string | null
}

export async function fetchCalendarStatus(): Promise<CalendarStatusResponse> {
  return withRefresh((token) =>
    authedFetch<CalendarStatusResponse>(`/calendar/status`, {}, token),
  )
}

export type CalendarBotStatusResponse = {
  enabled: boolean
  bot_email: string | null
  connected: boolean
}

export async function fetchCalendarBotStatus(): Promise<CalendarBotStatusResponse> {
  return withRefresh((token) =>
    authedFetch<CalendarBotStatusResponse>(`/calendar/bot-status`, {}, token),
  )
}

export async function startCalendarOAuth(): Promise<CalendarOAuthConnectResponse> {
  return withRefresh((token) =>
    authedFetch<CalendarOAuthConnectResponse>(
      `/calendar/oauth/connect`,
      {},
      token,
    ),
  )
}

export async function disconnectCalendar(): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(`/calendar/disconnect`, { method: 'POST' }, token),
  )
}

export type CalendarResyncResponse = {
  job_id: string
  /** Sopralluoghi da oggi in poi da riallineare (gia' su Calendar o in attesa/confermati). */
  candidates: number
}

/** Admin: porta i sopralluoghi al formato calendario attuale (aggiorna + inserisce i mancanti). */
export async function resyncCalendarEvents(): Promise<CalendarResyncResponse> {
  return withRefresh((token) =>
    authedFetch<CalendarResyncResponse>(
      `/calendar/resync`,
      { method: 'POST' },
      token,
    ),
  )
}

export async function syncAppointmentToCalendar(
  id: string,
): Promise<CalendarSyncResponse> {
  return withRefresh((token) =>
    authedFetch<CalendarSyncResponse>(
      `/calendar/sync/${id}`,
      { method: 'POST' },
      token,
    ),
  )
}

export async function unsyncAppointmentFromCalendar(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(
      `/calendar/sync/${id}`,
      { method: 'DELETE' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Google Calendar — list events on user's primary calendar (dashboard agenda)
// ---------------------------------------------------------------------------

export type CalendarEventItem = {
  id: string
  summary: string | null
  description: string | null
  location: string | null
  start: string | null // ISO datetime OR YYYY-MM-DD if all_day
  end: string | null
  all_day: boolean
  html_link: string | null
}

export type CalendarEventsListResponse = {
  items: CalendarEventItem[]
  connected: boolean
}

export async function fetchGoogleCalendarEvents(
  from: string,
  to: string,
): Promise<CalendarEventsListResponse> {
  const qs = new URLSearchParams({ from, to }).toString()
  return withRefresh((token) =>
    authedFetch<CalendarEventsListResponse>(
      `/calendar/events?${qs}`,
      {},
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// KPI metrics (Slice 28)
// ---------------------------------------------------------------------------

export type MetricCell = {
  metric_key: string
  actual: number
  target: number | null
  is_inverse: boolean
  progress_pct: number | null
  events_count?: number
  goal_id?: string | null
}

export type UserMetricsRow = {
  user_id: string
  user_name: string
  metrics: Record<string, MetricCell>
}

export type TeamAggregateRow = {
  metrics: Record<string, MetricCell>
}

export type PeriodInfo = {
  id: string
  label: string
  previous_id: string | null
  start: string
  end: string
}

export type MetricsSummaryResponse = {
  period_id: string
  period_start: string
  period_end: string
  metric_keys: string[]
  rows: UserMetricsRow[]
  period?: PeriodInfo | null
  team_avg?: TeamAggregateRow | null
  team_total?: TeamAggregateRow | null
  targets_editable?: boolean
}

export type MetricEventRow = {
  id: number
  user_id: string
  user_name: string | null
  metric_key: string
  delta: number
  source_type: string | null
  source_id: string | null
  occurred_at: string
}

export type MetricEventListResponse = {
  items: MetricEventRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type MetricEventListParams = {
  user_id?: string
  metric_key?: string
  period_id?: string
  page?: number
  page_size?: number
}

export type MetricGoalCreate = {
  user_id: string
  metric_key: string
  period_id: string
  target: number
  is_inverse?: boolean
}

export type MetricGoalResponse = {
  user_id: string
  metric_key: string
  period_id: string
  target: number
  is_inverse: boolean
}

export async function fetchMetricsSummary(
  period_id?: string,
  user_id?: string,
): Promise<MetricsSummaryResponse> {
  const qs = buildQuery({ period_id, user_id })
  return withRefresh((token) =>
    authedFetch<MetricsSummaryResponse>(`/metrics/summary${qs}`, {}, token),
  )
}

export async function fetchMetricsEvents(
  params: MetricEventListParams,
): Promise<MetricEventListResponse> {
  const qs = buildQuery({
    user_id: params.user_id,
    metric_key: params.metric_key,
    period_id: params.period_id,
    page: params.page,
    page_size: params.page_size,
  })
  return withRefresh((token) =>
    authedFetch<MetricEventListResponse>(`/metrics/events${qs}`, {}, token),
  )
}

export async function createMetricGoal(
  body: MetricGoalCreate,
): Promise<MetricGoalResponse> {
  return withRefresh((token) =>
    authedFetch<MetricGoalResponse>(
      '/metrics/goals',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export type MetricGoalPatchPayload = {
  target?: number
  is_inverse?: boolean
}

export async function patchMetricGoal(
  goalId: string,
  body: MetricGoalPatchPayload,
): Promise<MetricGoalResponse> {
  return withRefresh((token) =>
    authedFetch<MetricGoalResponse>(
      `/metrics/goals/${goalId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export type NotificationKind =
  | 'new_match'
  | 'sopralluogo_richiesto'
  | 'sopralluogo_fissato'
  | 'sollecito_custode'
  | 'asta_imminente'
  | 'partecipazione_aggiudicata'
  | 'partecipazione_persa'
  | 'generic'
  | 'comment_mention'
  | 'comment_reply'
  | 'comment_thread'
  | 'new_client'
  | 'new_participation'
  | 'lotto_mutuabile'

export type NotificationRow = {
  id: string
  kind: NotificationKind | string
  title: string
  body: string | null
  payload: Record<string, unknown> | null
  target_url: string | null
  read: boolean
  read_at: string | null
  created_at: string
}

export type NotificationListResponse = {
  items: NotificationRow[]
  total: number
  unread_count: number
  page: number
  page_size: number
  pages: number
}

export type NotificationCategory = 'all' | 'task' | 'message'

export async function fetchNotifications(
  unread_only: boolean = false,
  limit: number = 20,
  category: NotificationCategory = 'all',
): Promise<NotificationListResponse> {
  const qs = buildQuery({
    unread_only: unread_only ? 'true' : undefined,
    limit,
    category: category === 'all' ? undefined : category,
  })
  return withRefresh((token) =>
    authedFetch<NotificationListResponse>(
      `/notifications${qs}`,
      { method: 'GET' },
      token,
    ),
  )
}

export async function markNotificationRead(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(
      `/notifications/${id}/read`,
      { method: 'POST' },
      token,
    ),
  )
}

export async function markAllNotificationsRead(
  category: NotificationCategory = 'all',
): Promise<{ updated: number }> {
  const qs = buildQuery({ category: category === 'all' ? undefined : category })
  return withRefresh((token) =>
    authedFetch<{ updated: number }>(
      `/notifications/mark-all-read${qs}`,
      { method: 'POST' },
      token,
    ),
  )
}

// SSE via fetch+ReadableStream (EventSource doesn't support Authorization header).
// Yields parsed `event: notification` payloads. AbortController for cleanup.
export type NotifStreamMessage = { id: string; kind: string; title: string; body: string | null; target_url: string | null; created_at: string }

export async function openNotificationsStream(
  signal: AbortSignal,
  onMessage: (msg: NotifStreamMessage) => void,
  onError?: (err: unknown) => void,
): Promise<void> {
  const access = getAccessToken()
  if (!access) return
  try {
    const res = await fetch(`${BASE_URL}/notifications/stream`, {
      headers: { Authorization: `Bearer ${access}`, Accept: 'text/event-stream' },
      signal,
    })
    if (!res.ok || !res.body) {
      onError?.(new ApiError(res.status, 'SSE connect failed'))
      return
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let idx: number
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, idx)
        buffer = buffer.slice(idx + 2)
        let event = 'message'
        let data = ''
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        if (event === 'notification' && data) {
          try {
            const parsed = JSON.parse(data) as NotifStreamMessage
            onMessage(parsed)
          } catch {
            // ignore malformed
          }
        }
      }
    }
  } catch (err) {
    if (signal.aborted) return
    onError?.(err)
  }
}

// ---------------------------------------------------------------------------
// Pipeline drag-drop (Slice 27 fix D)
// ---------------------------------------------------------------------------

export async function moveCard(id: string, stage: string): Promise<{ id: string; stage: string; moved: boolean }> {
  return withRefresh((token) =>
    authedFetch<{ id: string; stage: string; moved: boolean }>(
      `/pipeline/cards/${id}/move`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage }),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Change password (Slice 31 audit security)
// ---------------------------------------------------------------------------

export type ChangePasswordPayload = {
  current_password: string
  new_password: string
}

export async function changePassword(body: ChangePasswordPayload): Promise<MeResponse> {
  return withRefresh((token) =>
    authedFetch<MeResponse>(
      `/auth/change-password`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Message templates (WhatsApp/Email)
// ---------------------------------------------------------------------------

export type MessageTemplateRow = {
  id: string
  code: string
  name: string
  channel: 'whatsapp' | 'email' | 'both'
  subject: string | null
  body_markdown: string
  placeholders: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export type MessageTemplateListResponse = {
  items: MessageTemplateRow[]
  total: number
}

export type RenderTemplatePayload = {
  context: Record<string, string | number | null>
  phone?: string | null
}

export type RenderTemplateResponse = {
  subject: string | null
  body: string
  wa_link: string
  missing_placeholders: string[]
}

export async function fetchTemplates(
  channel?: 'whatsapp' | 'email' | 'both',
): Promise<MessageTemplateListResponse> {
  const qs = buildQuery({ channel })
  return withRefresh((token) =>
    authedFetch<MessageTemplateListResponse>(
      `/templates${qs}`,
      { method: 'GET' },
      token,
    ),
  )
}

export async function renderTemplate(
  code: string,
  body: RenderTemplatePayload,
): Promise<RenderTemplateResponse> {
  return withRefresh((token) =>
    authedFetch<RenderTemplateResponse>(
      `/templates/${code}/render`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export type MessageTemplatePatch = {
  name?: string
  channel?: 'whatsapp' | 'email' | 'both'
  subject?: string | null
  body_markdown?: string
  is_active?: boolean
}

export async function patchTemplate(
  id: string,
  patch: MessageTemplatePatch,
): Promise<MessageTemplateRow> {
  return withRefresh((token) =>
    authedFetch<MessageTemplateRow>(
      `/templates/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
      token,
    ),
  )
}

export type SendEmailPayload = {
  to: string
  subject: string
  body: string
  template_code?: string
  client_id?: string
  property_id?: string
  participation_id?: string
  appointment_id?: string
}

export type SendEmailResponse = {
  id?: string
  dry_run?: boolean
  error?: string
  to?: string
  subject?: string
}

// ---------------------------------------------------------------------------
// Comments (chat interna pratica)
// ---------------------------------------------------------------------------

export type CommentEntityType =
  | 'client'
  | 'property'
  | 'appointment'
  | 'participation'

export type CommentRow = {
  id: string
  entity_type: CommentEntityType
  entity_id: string
  user_id: string | null
  user_name: string | null
  is_system_bot: boolean
  body: string
  parent_id: string | null
  mentions: string[]
  edited_at: string | null
  created_at: string
}

export type CommentListResponse = {
  items: CommentRow[]
  total: number
}

export type CommentCreatePayload = {
  entity_type: CommentEntityType
  entity_id: string
  body: string
  parent_id?: string | null
}

export type CommentPatchPayload = {
  body: string
}

export async function fetchComments(
  entity_type: CommentEntityType,
  entity_id: string,
): Promise<CommentListResponse> {
  const qs = buildQuery({ entity_type, entity_id })
  return withRefresh((token) =>
    authedFetch<CommentListResponse>(
      `/comments${qs}`,
      { method: 'GET' },
      token,
    ),
  )
}

export async function fetchCommentsStreamClient(
  client_id: string,
): Promise<CommentListResponse> {
  return withRefresh((token) =>
    authedFetch<CommentListResponse>(
      `/comments/stream/client/${client_id}`,
      { method: 'GET' },
      token,
    ),
  )
}

export async function createComment(
  body: CommentCreatePayload,
): Promise<CommentRow> {
  return withRefresh((token) =>
    authedFetch<CommentRow>(
      `/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function patchComment(
  id: string,
  patch: CommentPatchPayload,
): Promise<CommentRow> {
  return withRefresh((token) =>
    authedFetch<CommentRow>(
      `/comments/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
      token,
    ),
  )
}

export async function deleteComment(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<unknown>(`/comments/${id}`, { method: 'DELETE' }, token),
  )
}

export type WatchToggleResponse = {
  entity_type: CommentEntityType
  entity_id: string
  user_id: string
  muted: boolean
  watching: boolean
}

export async function toggleWatch(
  entity_type: CommentEntityType,
  entity_id: string,
  muted?: boolean,
): Promise<WatchToggleResponse> {
  return withRefresh((token) =>
    authedFetch<WatchToggleResponse>(
      `/comments/watch`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_type, entity_id, muted }),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Users (mention picker)
// ---------------------------------------------------------------------------

export type UserMini = {
  id: string
  name: string
  surname: string | null
  email: string
  role: string
  display_name: string
  avatar_url?: string | null
}

export type UserListResponse = {
  items: UserMini[]
  total: number
}

export async function fetchUsers(q?: string): Promise<UserListResponse> {
  const qs = buildQuery({ q, limit: 100 })
  return withRefresh((token) =>
    authedFetch<UserListResponse>(`/users${qs}`, { method: 'GET' }, token),
  )
}

export type ZoneRow = {
  id: string
  tag: string
  macro_area: string
  quartieri: string[]
}

export type ZoneListResponse = {
  items: ZoneRow[]
  total: number
}

export async function fetchZones(q?: string): Promise<ZoneListResponse> {
  const qs = buildQuery({ q, limit: 200 })
  return withRefresh((token) =>
    authedFetch<ZoneListResponse>(`/zones${qs}`, { method: 'GET' }, token),
  )
}

export async function sendEmail(
  body: SendEmailPayload,
): Promise<SendEmailResponse> {
  return withRefresh((token) =>
    authedFetch<SendEmailResponse>(
      `/templates/send-email`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Messages log (outbound WA + email cronologia)
// ---------------------------------------------------------------------------

export type MessageKind = 'whatsapp' | 'email' | 'internal'
export type MessageStatus =
  | 'link_generated'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'bounced'

export type MessageLogPayload = {
  kind: MessageKind
  body: string
  template_code?: string
  subject?: string
  phone_to?: string
  email_to?: string
  client_id?: string
  property_id?: string
  participation_id?: string
  appointment_id?: string
  conversation_id?: string
  status?: MessageStatus
  meta?: Record<string, unknown>
}

export type MessageAttachment = {
  id: string
  filename: string
  mime_type: string
  size_bytes: number
  is_image: boolean
  url: string
  width?: number | null
  height?: number | null
}

export type MessageRow = {
  id: string
  kind: string
  template_code: string | null
  subject: string | null
  body: string
  phone_to: string | null
  email_to: string | null
  client_id: string | null
  client_name: string | null
  client_short_code: number | null
  property_id: string | null
  property_address: string | null
  property_short_code: number | null
  participation_id: string | null
  appointment_id: string | null
  sent_by: string | null
  sent_by_name: string | null
  sent_at: string
  status: string
  attachments?: MessageAttachment[]
}

export type MessageListResponse = {
  items: MessageRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type MessageListParams = {
  page?: number
  page_size?: number
  q?: string
  kind?: MessageKind
  client_id?: string
  property_id?: string
  internal_with_user_id?: string
  conversation_id?: string
}

/** Fire-and-forget log of an outbound message. Silent on error — UI must not block. */
export async function logMessage(
  payload: MessageLogPayload,
): Promise<MessageRow | null> {
  try {
    return await withRefresh((token) =>
      authedFetch<MessageRow>(
        '/messages',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
        token,
      ),
    )
  } catch {
    // Logging failures should never break the user-facing send. Drop quietly.
    return null
  }
}

export async function fetchMessages(
  params: MessageListParams,
): Promise<MessageListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
    q: params.q,
    kind: params.kind,
    client_id: params.client_id,
    property_id: params.property_id,
    internal_with_user_id: params.internal_with_user_id,
    conversation_id: params.conversation_id,
  })
  return withRefresh((token) =>
    authedFetch<MessageListResponse>(`/messages${qs}`, {}, token),
  )
}

// Threads (Messaggi M1) ------------------------------------------------------
export type ThreadRow = {
  // Per thread cliente = client.id. Per thread internal = altro user.id.
  client_id: string
  client_name: string
  client_short_code: number
  last_message_id: string
  last_message_kind: string
  last_message_subject: string | null
  last_message_body_preview: string
  last_message_at: string
  last_message_sent_by_name: string | null
  last_message_property_address: string | null
  last_message_property_short_code: number | null
  message_count: number
  thread_type?: 'client' | 'internal'
  pinned?: boolean
}

export type ThreadListResponse = {
  items: ThreadRow[]
  total: number
}

export type ThreadListParams = {
  limit?: number
  sent_by?: string
  sent_at_from?: string
  sent_at_to?: string
  status?: string
}

export async function fetchMessageThreads(
  params: ThreadListParams = {},
): Promise<ThreadListResponse> {
  const qs = buildQuery({
    limit: params.limit ?? 100,
    sent_by: params.sent_by,
    sent_at_from: params.sent_at_from,
    sent_at_to: params.sent_at_to,
    status: params.status,
  })
  return withRefresh((token) =>
    authedFetch<ThreadListResponse>(`/messages/threads${qs}`, {}, token),
  )
}

// ---------------------------------------------------------------------------
// Conversations — chat interne di gruppo agganciate a un contesto
// ---------------------------------------------------------------------------

export type ConversationContextType =
  | 'property'
  | 'client'
  | 'participation'
  | 'appointment'

export type ConvParticipant = {
  user_id: string
  name: string | null
  left: boolean
  /** ISO ultima lettura del partecipante (per doppia spunta lato mittente). */
  last_read_at?: string | null
}

export type ConversationRow = {
  id: string
  context_type: ConversationContextType | null
  property_id: string | null
  client_id: string | null
  participation_id: string | null
  appointment_id: string | null
  title: string
  participants: ConvParticipant[]
  last_message_preview: string | null
  last_message_at: string | null
  unread_count: number
  updated_at: string
  is_group?: boolean
  created_by?: string | null
  pinned_message?: { id: string; body: string; sent_by_name: string | null } | null
  pinned?: boolean
}

export type ConversationListResponse = {
  items: ConversationRow[]
  total: number
}

export type ConversationCreatePayload = {
  context_type?: ConversationContextType | null
  property_id?: string | null
  client_id?: string | null
  participation_id?: string | null
  appointment_id?: string | null
  participant_ids: string[]
  title?: string | null
}

export async function fetchConversations(): Promise<ConversationListResponse> {
  return withRefresh((token) =>
    authedFetch<ConversationListResponse>('/conversations', {}, token),
  )
}

export async function fetchConversation(id: string): Promise<ConversationRow> {
  return withRefresh((token) =>
    authedFetch<ConversationRow>(`/conversations/${id}`, {}, token),
  )
}

export async function createConversation(
  payload: ConversationCreatePayload,
): Promise<ConversationRow> {
  return withRefresh((token) =>
    authedFetch<ConversationRow>(
      '/conversations',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      token,
    ),
  )
}

export async function addConversationParticipants(
  id: string,
  user_ids: string[],
): Promise<ConversationRow> {
  return withRefresh((token) =>
    authedFetch<ConversationRow>(
      `/conversations/${id}/participants`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids }),
      },
      token,
    ),
  )
}

export async function kickParticipant(
  conversationId: string,
  userId: string,
): Promise<void> {
  await withRefresh((token) =>
    authedFetch<void>(
      `/conversations/${conversationId}/participants/${userId}`,
      { method: 'DELETE' },
      token,
    ),
  )
}

export async function pinChat(
  targetKind: 'conversation' | 'client',
  targetId: string,
  pinned: boolean,
): Promise<void> {
  await withRefresh((token) =>
    authedFetch<void>(
      '/chat-pins',
      {
        method: pinned ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_kind: targetKind, target_id: targetId }),
      },
      token,
    ),
  )
}

export async function pinMessage(
  conversationId: string,
  messageId: string | null,
): Promise<ConversationRow> {
  return withRefresh((token) =>
    authedFetch<ConversationRow>(
      `/conversations/${conversationId}/pin`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: messageId }),
      },
      token,
    ),
  )
}

export async function leaveConversation(
  id: string,
  newOwnerId?: string,
): Promise<void> {
  const qs = newOwnerId ? `?new_owner_id=${encodeURIComponent(newOwnerId)}` : ''
  await withRefresh((token) =>
    authedFetch<void>(
      `/conversations/${id}/participants/me${qs}`,
      { method: 'DELETE' },
      token,
    ),
  )
}

export async function markConversationRead(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<void>(`/conversations/${id}/read`, { method: 'PATCH' }, token),
  )
}

export async function renameConversation(
  id: string,
  title: string,
): Promise<ConversationRow> {
  return withRefresh((token) =>
    authedFetch<ConversationRow>(
      `/conversations/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      },
      token,
    ),
  )
}

/** Elimina la conversazione per tutti i partecipanti (DELETE /conversations/{id}). */
export async function deleteConversation(id: string): Promise<void> {
  await withRefresh((token) =>
    authedFetch<void>(`/conversations/${id}`, { method: 'DELETE' }, token),
  )
}

/** Invia un messaggio interno dentro una conversazione (POST /messages). */
export async function sendConversationMessage(
  conversation_id: string,
  body: string,
  attachment_ids?: string[],
): Promise<MessageRow> {
  return withRefresh((token) =>
    authedFetch<MessageRow>(
      '/messages',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'internal',
          body,
          conversation_id,
          attachment_ids,
        }),
      },
      token,
    ),
  )
}

async function uploadConversationAttachmentRaw(
  token: string,
  conversationId: string,
  file: File,
): Promise<MessageAttachment> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(
    `${BASE_URL}/conversations/${conversationId}/attachments`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    },
  )
  const payload = await parseJson(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      pickErrorMessage(payload, `HTTP ${res.status}`),
      payload,
    )
  }
  return payload as MessageAttachment
}

export async function uploadConversationAttachment(
  conversationId: string,
  file: File,
): Promise<MessageAttachment> {
  const access = getAccessToken()
  if (!access) throw new ApiError(401, 'No access token')
  try {
    return await uploadConversationAttachmentRaw(access, conversationId, file)
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearTokens()
      throw err
    }
    try {
      const tokens = await refresh(refreshToken)
      setTokens(tokens.access_token, tokens.refresh_token)
      return await uploadConversationAttachmentRaw(
        tokens.access_token,
        conversationId,
        file,
      )
    } catch (refreshErr) {
      clearTokens()
      throw refreshErr
    }
  }
}

export async function deleteConversationAttachment(
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  await withRefresh((token) =>
    authedFetch<void>(
      `/conversations/${conversationId}/attachments/${attachmentId}`,
      { method: 'DELETE' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Client score — Spec #1, GET /clients/{id}/score
// ---------------------------------------------------------------------------

export type ClientScoreBreakdown = {
  engagement: number
  budget_fit: number
  compat_tech: number
}

export type ClientScore = {
  score: number
  grade: string
  delta_30g: number
  trend_30g: number[]
  breakdown: ClientScoreBreakdown
}

export async function fetchClientScore(id: string): Promise<ClientScore> {
  return withRefresh((token) =>
    authedFetch<ClientScore>(`/clients/${id}/score`, {}, token),
  )
}

// Completezza profilo per consulente — KPI Giulia (full-view).
export type ConsultantCompleteness = {
  consultant_id: string | null
  consultant_name: string | null
  total: number
  incompleti: number
}

export async function fetchCompletenessByConsultant(): Promise<
  ConsultantCompleteness[]
> {
  return withRefresh((token) =>
    authedFetch<ConsultantCompleteness[]>(
      '/metrics/completeness-by-consultant',
      {},
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Activity feed — Spec #4, GET /activity
// ---------------------------------------------------------------------------

export type ActivityKind =
  | 'call'
  | 'email'
  | 'meet'
  | 'sopralluogo'
  | 'scd'
  | 'partecipazione'
  | 'messaggio_wa'
  | 'messaggio_email'
  | 'sistema'
  | 'edit'
  | 'login'
  | 'commento'
  | 'allegato_upload'
  | 'match'
  | 'proposta'
  | 'privacy_change'

export type ActivityEventRow = {
  id: string
  occurred_at: string
  kind: string
  actor_user_id: string | null
  actor_name: string | null
  client_id: string | null
  property_id: string | null
  participation_id: string | null
  appointment_id: string | null
  title: string
  body: string | null
  payload: Record<string, unknown> | null
  channel: string | null
  durata_min: number | null
  esito: string | null
  attachments_count: number
}

export type ActivityListResponse = {
  items: ActivityEventRow[]
  total: number
  page: number
  page_size: number
  pages: number
}

export type ActivityListParams = {
  client_id?: string
  property_id?: string
  user_id?: string
  kind?: ActivityKind
  page?: number
  page_size?: number
}

export async function fetchActivity(
  params: ActivityListParams,
): Promise<ActivityListResponse> {
  const qs = buildQuery({
    client_id: params.client_id,
    property_id: params.property_id,
    user_id: params.user_id,
    kind: params.kind,
    page: params.page,
    page_size: params.page_size,
  })
  return withRefresh((token) =>
    authedFetch<ActivityListResponse>(`/activity${qs}`, {}, token),
  )
}

// ---------------------------------------------------------------------------
// Property photos — Spec #2, GET /properties/{id}/photos
// ---------------------------------------------------------------------------

export type PropertyPhotoSource = 'ctu' | 'scrape' | 'sopralluogo' | 'manual'

export type PropertyPhoto = {
  id: string
  property_id: string
  url_full: string
  url_thumb: string
  tag: string | null
  order: number
  source: string
  width: number | null
  height: number | null
  size_bytes: number | null
  mime_type: string | null
  created_at: string
}

export type PropertyPhotoListResponse = {
  items: PropertyPhoto[]
  total: number
  page: number
  page_size: number
  pages: number
}

export async function fetchPropertyPhotos(
  propertyId: string,
  params: { page?: number; page_size?: number } = {},
): Promise<PropertyPhotoListResponse> {
  const qs = buildQuery({
    page: params.page,
    page_size: params.page_size,
  })
  return withRefresh((token) =>
    authedFetch<PropertyPhotoListResponse>(
      `/properties/${propertyId}/photos${qs}`,
      {},
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Property map POIs — Spec #2, GET /properties/{id}/map-pois
// ---------------------------------------------------------------------------

export type MapPoiRow = {
  tipo: string
  distanza_m: number
  nome: string
}

export type MapIsocrone = {
  metro: { distanza_m: number; label: string } | null
  scuole_1km: number
  market_500m: number
}

export type MapPoisResponse = {
  lat: number
  lng: number
  pois: MapPoiRow[]
  isocrone: MapIsocrone
  cached_at: string | null
  cache_age_hours: number | null
}

export async function fetchPropertyMapPois(
  propertyId: string,
  refresh = false,
): Promise<MapPoisResponse> {
  const qs = refresh ? '?refresh=true' : ''
  return withRefresh((token) =>
    authedFetch<MapPoisResponse>(
      `/properties/${propertyId}/map-pois${qs}`,
      {},
      token,
    ),
  )
}

// On-demand Nominatim geocoding from the lotto detail map placeholder.
// Returns the freshly resolved coords + quality flag.
export type GeocodeResponse = {
  lat: number | null
  lng: number | null
  quality: 'exact' | 'partial' | 'fallback' | 'none'
  display_name: string | null
}

export async function geocodeProperty(
  propertyId: string,
): Promise<GeocodeResponse> {
  return withRefresh((token) =>
    authedFetch<GeocodeResponse>(
      `/properties/${propertyId}/geocode`,
      { method: 'POST' },
      token,
    ),
  )
}

export type ResolveZoneResponse = {
  status: 'auto' | 'ambiguous' | 'unresolved' | null
  tag: string | null
  candidates: string[]
}

export async function resolveZone(
  propertyId: string,
): Promise<ResolveZoneResponse> {
  return withRefresh((token) =>
    authedFetch<ResolveZoneResponse>(
      `/properties/${propertyId}/resolve-zone`,
      { method: 'POST' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// /me extended — Spec #3 (separate from /auth/me which stays lean).
// ---------------------------------------------------------------------------

export async function fetchMeExtended(): Promise<MeResponse> {
  return withRefresh((token) => authedFetch<MeResponse>('/me', {}, token))
}

export async function patchMe(body: MePatchPayload): Promise<MeResponse> {
  return withRefresh((token) =>
    authedFetch<MeResponse>(
      '/me',
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function uploadAvatar(file: File): Promise<AvatarResponse> {
  const fd = new FormData()
  fd.append('file', file)
  return withRefresh((token) =>
    authedFetch<AvatarResponse>(
      '/me/avatar',
      {
        method: 'POST',
        body: fd,
      },
      token,
    ),
  )
}

export async function deleteAvatar(): Promise<void> {
  return withRefresh(async (token) => {
    const res = await fetch(`${BASE_URL}/me/avatar`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok && res.status !== 204) {
      const payload = await parseJson(res)
      throw new ApiError(
        res.status,
        pickErrorMessage(payload, `HTTP ${res.status}`),
        payload,
      )
    }
  })
}

export async function fetchMeSessions(): Promise<MeSessionListResponse> {
  return withRefresh((token) =>
    authedFetch<MeSessionListResponse>('/me/sessions', {}, token),
  )
}

export async function deleteMeSession(sessionId: string): Promise<void> {
  return withRefresh(async (token) => {
    const res = await fetch(`${BASE_URL}/me/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok && res.status !== 204) {
      const payload = await parseJson(res)
      throw new ApiError(
        res.status,
        pickErrorMessage(payload, `HTTP ${res.status}`),
        payload,
      )
    }
  })
}

export async function deleteAllMeSessions(): Promise<void> {
  return withRefresh(async (token) => {
    const res = await fetch(`${BASE_URL}/me/sessions`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok && res.status !== 204) {
      const payload = await parseJson(res)
      throw new ApiError(
        res.status,
        pickErrorMessage(payload, `HTTP ${res.status}`),
        payload,
      )
    }
  })
}

export async function fetchAccessLog(
  limit = 10,
): Promise<AccessLogEntry[]> {
  return withRefresh((token) =>
    authedFetch<AccessLogEntry[]>(
      `/me/access-log?limit=${limit}`,
      {},
      token,
    ),
  )
}

export async function regenerateBackupCodes(): Promise<BackupCodesResponse> {
  return withRefresh((token) =>
    authedFetch<BackupCodesResponse>(
      '/me/backup-codes/regenerate',
      { method: 'POST' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Saved views — Spec #7
// ---------------------------------------------------------------------------

export type SavedViewEntity =
  | 'lotti'
  | 'clienti'
  | 'sopralluoghi'
  | 'partecipazioni'
  | 'messaggi'
  | 'pipeline'

export type SavedViewRow = {
  id: string
  user_id: string
  entity_type: string
  name: string
  filters: Record<string, unknown>
  sort_order: number
  is_shared: boolean
  is_owner: boolean
  created_at: string
  updated_at: string
}

export type SavedViewCreate = {
  entity_type: SavedViewEntity
  name: string
  filters: Record<string, unknown>
  sort_order?: number
  is_shared?: boolean
}

export type SavedViewPatch = {
  name?: string
  filters?: Record<string, unknown>
  sort_order?: number
  is_shared?: boolean
}

export async function fetchSavedViews(
  entityType?: SavedViewEntity,
): Promise<SavedViewRow[]> {
  const qs = entityType ? `?entity_type=${entityType}` : ''
  return withRefresh((token) =>
    authedFetch<SavedViewRow[]>(`/saved-views${qs}`, {}, token),
  )
}

export async function createSavedView(
  body: SavedViewCreate,
): Promise<SavedViewRow> {
  return withRefresh((token) =>
    authedFetch<SavedViewRow>(
      '/saved-views',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function patchSavedView(
  id: string,
  body: SavedViewPatch,
): Promise<SavedViewRow> {
  return withRefresh((token) =>
    authedFetch<SavedViewRow>(
      `/saved-views/${id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

export async function deleteSavedView(id: string): Promise<void> {
  return withRefresh(async (token) => {
    const res = await fetch(`${BASE_URL}/saved-views/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok && res.status !== 204) {
      const payload = await parseJson(res)
      throw new ApiError(
        res.status,
        pickErrorMessage(payload, `HTTP ${res.status}`),
        payload,
      )
    }
  })
}

export async function toggleSavedViewShare(id: string): Promise<SavedViewRow> {
  return withRefresh((token) =>
    authedFetch<SavedViewRow>(
      `/saved-views/${id}/share`,
      { method: 'POST' },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Pipeline drag-drop — Spec #6
// ---------------------------------------------------------------------------

export type PipelineMovePayload = {
  from_stage: string
  to_stage: string
  position?: number
}

export type PipelineMoveResponse = {
  property_id: string
  stage: string
  updated_at: string
  audit_id?: string
}

export async function movePipelineCard(
  propertyId: string,
  body: PipelineMovePayload,
): Promise<PipelineMoveResponse> {
  return withRefresh((token) =>
    authedFetch<PipelineMoveResponse>(
      `/pipeline/cards/${propertyId}/move`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      token,
    ),
  )
}

// ---------------------------------------------------------------------------
// Fatturato mensile (dashboard CEO) — valori manuali
// ---------------------------------------------------------------------------

export type FatturatoRow = {
  anno: number
  mese: number
  previous: number | null
  actual: number | null
  forecast: number | null
}

export type FatturatoListResponse = {
  anno: number
  rows: FatturatoRow[]
}

export async function fetchFatturato(
  anno: number,
): Promise<FatturatoListResponse> {
  return withRefresh((token) =>
    authedFetch<FatturatoListResponse>(`/fatturato?anno=${anno}`, {}, token),
  )
}

export async function putFatturato(
  anno: number,
  rows: FatturatoRow[],
): Promise<FatturatoListResponse> {
  return withRefresh((token) =>
    authedFetch<FatturatoListResponse>(
      `/fatturato`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anno, rows }),
      },
      token,
    ),
  )
}

// ─── SCD Requests ──────────────────────────────────────────────────────────

export interface ScdChecklistItem {
  documento: string
  ricevuto: boolean
  ricevuto_at?: string | null
  allegato_s3_key?: string | null
  note?: string | null
}

export interface Professional {
  id: string
  organization_id: string
  tipo: string
  name: string
  email?: string | null
  pec?: string | null
  telefono?: string | null
  indirizzo?: string | null
  note?: string | null
  created_at: string
  updated_at: string
}

export interface ScdComment {
  id: string
  scd_request_id: string
  author_id: string | null
  author_name: string | null
  text: string
  created_at: string
}

export interface ScdRequest {
  id: string
  short_code: number
  organization_id: string
  client_id: string
  property_id: string
  stage: string
  scd_status?: string | null
  scd_priority_color?: string | null
  scd_da_fix?: boolean
  scd_days_to_asta?: number | null
  tipologia_asta?: string | null
  client_cognome?: string | null
  client_nome?: string | null
  property_short_code?: number | null
  property_rge?: string | null
  property_data_asta?: string | null
  property_address?: string | null
  property_comune?: string | null
  property_zona?: string | null
  property_mq?: number | null
  property_prezzo_base?: number | null
  owner_initials?: string | null
  budget_complessivo?: number | null
  liquidita?: number | null
  tipologia_acquirente?: string | null
  scopo_acquisto?: string | null
  sopralluogo_effettuato?: boolean
  mutuo_necessario?: boolean | null
  lorenzo_inviato?: boolean
  lorenzo_esito?: string | null
  mutuabile?: boolean | null
  tipo_vendita?: string | null
  note?: string | null
  data_presentazione?: string | null
  consulente_sollecitato?: boolean
  contattare_delegato?: boolean
  domanda_fatta?: boolean
  scadenza_redazione?: string | null
  scadenza_offerta?: string | null
  scd_presentata_at?: string | null
  scadenza_contatto_cliente?: string | null
  scadenza_checklist?: string | null
  origin_appointment_id?: string | null
  presentation_appointment_id?: string | null
  participation_id?: string | null
  outcome_reason?: string | null
  delegato_id?: string | null
  owner_id?: string | null
  owner_name?: string | null
  client_consultant_name?: string | null
  checklist_documenti?: ScdChecklistItem[] | null
  motivazione?: string | null
  version: number
  created_at: string
  updated_at: string
  // Detail-only fields (populated by GET /{id})
  delegato?: Professional | null
  comments?: ScdComment[]
  events?: ScdEventItem[]
  last_advanced_at?: string | null
  last_advanced_by?: string | null
}

export interface ScdBoard {
  stages: { stage: string; count: number; items: ScdRequest[] }[]
  total: number
}

export async function fetchScdBoard(filters?: {
  propertyId?: string
  clientId?: string
  stage?: string
}): Promise<ScdBoard> {
  const qs = new URLSearchParams()
  if (filters?.propertyId) qs.set('property_id', filters.propertyId)
  if (filters?.clientId) qs.set('client_id', filters.clientId)
  if (filters?.stage) qs.set('stage', filters.stage)
  return withRefresh((token) =>
    authedFetch<ScdBoard>(`/scd-requests?${qs.toString()}`, {}, token),
  )
}

export async function fetchScdRequest(scdId: string): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(`/scd-requests/${scdId}`, {}, token),
  )
}

export interface ScdEventItem {
  id: string
  scd_request_id: string
  actor_id: string | null
  actor_name: string | null
  kind: string
  label: string | null
  from_stage: string | null
  to_stage: string | null
  note: string | null
  created_at: string
}

/** @deprecated use ScdEventItem */
export type ScdEvent = ScdEventItem

export async function fetchScdEvents(scdId: string): Promise<ScdEvent[]> {
  return withRefresh((token) =>
    authedFetch<ScdEvent[]>(`/scd-requests/${scdId}/events`, {}, token),
  )
}

export interface ScdKpi {
  total: number
  active: number
  aggiudicata: number
  non_aggiudicata: number
  esito_neg_scd: number
  avg_age_days: number | null
  by_stage: { stage: string; count: number }[]
}

export async function fetchScdKpi(): Promise<ScdKpi> {
  return withRefresh((token) =>
    authedFetch<ScdKpi>('/scd-requests/kpi', {}, token),
  )
}

export async function createScdRequest(data: {
  client_id: string
  property_id: string
  budget_complessivo?: number | null
  liquidita?: number | null
  tipologia_acquirente?: 'prima_casa' | 'seconda_casa' | 'societa' | null
  scopo_acquisto?: string | null
  mutuo_necessario?: boolean | null
  sopralluogo_effettuato?: boolean
  tipo_vendita?: 'telematica' | 'fisica' | null
}): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      '/scd-requests',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function updateScdChecklist(
  scdId: string,
  checklist: ScdChecklistItem[],
  version: number,
): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      `/scd-requests/${scdId}/checklist`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ checklist_documenti: checklist, version }) },
      token,
    ),
  )
}

export async function moveScdRequest(
  scdId: string,
  data: {
    to_stage: string
    version: number
    scadenza_offerta?: string | null
    presentation_appointment_id?: string | null
    data_presentazione?: string | null
    motivazione?: string | null
  },
): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      `/scd-requests/${scdId}/move`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function setScdScadenza(
  scdId: string,
  data: { scadenza_redazione: string; version: number },
): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      `/scd-requests/${scdId}/scadenza`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function setScdStatus(
  scdId: string,
  data: { scd_status: string; version: number },
): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      `/scd-requests/${scdId}/scd-status`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function updateScdFields(
  scdId: string,
  data: Partial<Omit<ScdRequest, 'id' | 'short_code' | 'organization_id' | 'stage' | 'created_at' | 'updated_at'>> & { version: number },
): Promise<ScdRequest> {
  return withRefresh((token) =>
    authedFetch<ScdRequest>(
      `/scd-requests/${scdId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function fetchScdComments(scdId: string): Promise<ScdComment[]> {
  return withRefresh((token) =>
    authedFetch<ScdComment[]>(`/scd-requests/${scdId}/comments`, {}, token),
  )
}

export async function addScdComment(scdId: string, text: string): Promise<ScdComment> {
  return withRefresh((token) =>
    authedFetch<ScdComment>(
      `/scd-requests/${scdId}/comments`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) },
      token,
    ),
  )
}

export interface ScdAdjacent {
  prev_id: string | null
  next_id: string | null
  position: number | null
  total: number
}

export async function fetchScdAdjacent(
  scdId: string,
  filters?: { stage?: string; scd_status?: string; tipo_vendita?: string; consulente_sollecitato?: boolean },
): Promise<ScdAdjacent> {
  const qs = new URLSearchParams()
  if (filters?.stage) qs.set('stage', filters.stage)
  if (filters?.scd_status) qs.set('scd_status', filters.scd_status)
  if (filters?.tipo_vendita) qs.set('tipo_vendita', filters.tipo_vendita)
  if (filters?.consulente_sollecitato !== undefined) qs.set('consulente_sollecitato', String(filters.consulente_sollecitato))
  return withRefresh((token) =>
    authedFetch<ScdAdjacent>(`/scd-requests/${scdId}/adjacent?${qs.toString()}`, {}, token),
  )
}

export async function listProfessionals(params?: { tipo?: string; q?: string }): Promise<Professional[]> {
  const qs = new URLSearchParams()
  if (params?.tipo) qs.set('tipo', params.tipo)
  if (params?.q) qs.set('q', params.q)
  return withRefresh((token) =>
    authedFetch<Professional[]>(`/professionals?${qs.toString()}`, {}, token),
  )
}

export async function createProfessional(data: Omit<Professional, 'id' | 'organization_id' | 'created_at' | 'updated_at'>): Promise<Professional> {
  return withRefresh((token) =>
    authedFetch<Professional>(
      '/professionals',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}

export async function updateProfessional(id: string, data: Partial<Omit<Professional, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>): Promise<Professional> {
  return withRefresh((token) =>
    authedFetch<Professional>(
      `/professionals/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
      token,
    ),
  )
}
